import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import supabaseService from '../services/supabaseService';
import { supabase } from '../config/supabase';
import Toast from '../components/Toast';
import ClientsPanel from '../components/ClientsPanel';
import AvailabilityPanel from '../components/AvailabilityPanel';
import QuickBookModal from '../components/QuickBookModal';
import { formatAmount, formatCents, dollarsToCents } from '../utils/money';
import '../assets/styles/admin.css';
import '../assets/styles/quickbook.css';

// The move picker pages a fortnight at a time, same as the public booking
// form — she reads one screen of days rather than a month-long list.
const MOVE_DAYS_PER_PAGE = 14;

// Local date parts, never toISOString(). NZ is UTC+12/13, so a UTC date string
// is yesterday's date for the whole first half of Viktoria's working day.
const toISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (d, n) => {
  const copy = new Date(d.getTime());
  copy.setDate(copy.getDate() + n);
  return copy;
};

// "2029-08-18" → "18 Aug 2029". Split as text, not handed to new Date(): a
// bare YYYY-MM-DD is read as UTC midnight, which is the previous day's date
// anywhere west of Greenwich and the wrong day in half the world's rendering.
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatISODate = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-');
  const month = MONTH_NAMES[Number(m) - 1];
  if (!y || !month || !d) return iso || '';
  return `${Number(d)} ${month} ${y}`;
};

// ── Blocked-out time ──────────────────────────────────────────────────────
// time_off.start_at / end_at are Postgres `timestamp` WITHOUT time zone holding
// NZ wall clock ("2026-08-20T14:00:00"). They are split as text on purpose —
// handing one to new Date() has the browser read it as local or as UTC
// depending on its shape and shift it half a day either way. Same approach as
// AvailabilityPanel, which writes these rows.
const splitStamp = (stamp) => {
  const s = String(stamp || '').replace(' ', 'T');
  const [date, time = '00:00'] = s.split('T');
  return { date: date.slice(0, 10), time: time.slice(0, 5) };
};

const minutesOfTime = (value) => {
  const [h, m] = String(value || '').slice(0, 5).split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : 0;
};

// Whole-day blocks are written as 00:00 → 23:59 (see AvailabilityPanel), so
// they are recognised by that shape rather than by a column the table has not
// got.
const isWholeDay = (from, to) => from === '00:00' && to === '23:59';

// The piece of a block that lands on one particular date. A block can run
// across several days, so it is clipped: the first day starts at its start
// time, the last day ends at its end time, and every day in between is whole.
// Returns null when the block does not touch this date at all.
// ISO dates compare correctly as strings, which keeps Date out of it entirely.
const timeOffOnDate = (row, isoDate) => {
  const start = splitStamp(row.start_at);
  const end = splitStamp(row.end_at);
  if (!start.date || !end.date || isoDate < start.date || isoDate > end.date) return null;
  const from = isoDate === start.date ? start.time : '00:00';
  const to = isoDate === end.date ? end.time : '23:59';
  return { from, to, start, end, allDay: isWholeDay(from, to), spansDays: start.date !== end.date };
};

// Identifies which span of dates the loaded blocks belong to, so a tab switch
// cannot render last month's blocks against today for a frame.
const rangeKey = (range) => (range ? `${range.start}..${range.end}` : '');

// ── Blocks that came from Google Calendar ─────────────────────────────────
// Deliberately NOT splitStamp. These two look similar and are not:
//
//   time_off.start_at   "2026-08-20T14:00:00"        no zone, NZ wall clock
//   busy period start   "2026-09-04T00:00:00+12:00"  carries its own offset
//
// The zone-less one must stay text — reading it through Date shifts it 12 or 13
// hours. The Google one is the opposite case: it genuinely carries a zone, so
// it is parsed properly and then asked what time that was in Auckland. Routing
// either through the other's helper is a half-day error.
const NZ_WALL_CLOCK = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

const nzWallClock = (iso) => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const parts = {};
  for (const part of NZ_WALL_CLOCK.formatToParts(at)) parts[part.type] = part.value;
  // Some engines render midnight as hour 24 of the previous day under hour12:
  // false, and some ignore the 2-digit request and hand back a bare "0". Both
  // are normalised here, because everything downstream compares these as text —
  // "0:00" would not equal "00:00" and a whole-day block would stop reading as
  // one.
  const hour = String(parts.hour === '24' ? 0 : parts.hour).padStart(2, '0');
  const minute = String(parts.minute).padStart(2, '0');
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${hour}:${minute}` };
};

const shiftISODate = (isoDate, days) => toISODate(addDays(new Date(`${isoDate}T00:00:00`), days));

// One busy period from the calendar, turned into the same shape the rest of
// this file draws blocks in.
//
// The trap is `end`: it is EXCLUSIVE. A block on Friday the 4th alone comes back
// as the 4th at 00:00 through to the 5th at 00:00, and drawn literally that is a
// two-day block covering a Saturday she is actually working. Anything finishing
// exactly on midnight finished the day before — true of a timed event ending at
// 23:00–00:00 just as much as of an all-day one, so the rollback is unconditional.
//
// Whether a period is all-day is taken from the `allDay` flag and never guessed
// from how the string looks: an all-day period is one we format ourselves, but a
// timed one is Google's own `dateTime` verbatim, and the two are not written the
// same way.
const googleBlockFromPeriod = (period, index) => {
  const start = nzWallClock(period?.start);
  const rawEnd = nzWallClock(period?.end);
  if (!start || !rawEnd) return null;

  const endsOnMidnight = rawEnd.time === '00:00';
  const endDate = endsOnMidnight ? shiftISODate(rawEnd.date, -1) : rawEnd.date;
  const endTime = endsOnMidnight ? '23:59' : rawEnd.time;
  // A zero-length event occupies nothing.
  if (endDate < start.date) return null;

  return {
    id: `google-${index}`,
    summary: period.summary || '',
    startDate: start.date,
    startTime: start.time,
    endDate,
    endTime,
    allDay: Boolean(period.allDay),
    spansDays: endDate !== start.date,
  };
};

// The piece of a Google block that lands on one date — same clipping rule as
// timeOffOnDate, and null when it does not touch that date at all.
//
// `allDay` here is about this one date's slice, not about the event: a middle
// day of a three-day block is a whole day whatever the event is flagged as, and
// that is a wording decision, not a parsing one.
const googleBlockOnDate = (block, isoDate) => {
  if (isoDate < block.startDate || isoDate > block.endDate) return null;
  const from = isoDate === block.startDate ? block.startTime : '00:00';
  const to = isoDate === block.endDate ? block.endTime : '23:59';
  return { from, to, allDay: block.allDay || isWholeDay(from, to), spansDays: block.spansDays };
};

// The window a booking takes up in the Google calendar: the appointment plus her
// rest, because that is what booking-notification writes there.
const bookingCalendarSpan = (booking) => {
  if (!booking?.date || !booking?.time) return null;
  const from = minutesOfTime(booking.time);
  const length = (Number(booking.total_duration) || 60) + (Number(booking.rest_minutes) || 0);
  return { date: booking.date, from, to: from + length };
};

// How far a calendar event's start may sit from a booking's own start and still
// be that booking. Absorbs a start nudged by hand in Google, and an event
// written before a treatment's duration was edited.
//
// Small on purpose, and anchored on the start rather than padding both ends:
// padding by half an hour either way made a 60-minute appointment claim 09:30
// to 11:30, which silently swallowed a genuine personal entry at 11:00 that
// clashed with nothing — and a swallowed entry is a day she thinks is free.
const ECHO_START_TOLERANCE_MINUTES = 10;

// Every approved booking is written into the same Google calendar we are reading
// back, so each appointment on this screen also returns as a busy period. Drawn
// as well, the day reads twice.
//
// A plain overlap test would do it, and would quietly reintroduce the exact bug
// this screen exists to fix: an all-day block on a day that also has one
// appointment overlaps that appointment, so it would be dropped — and the
// blocked day would look free again. So the test is containment, not overlap:
// an appointment-shaped period disappears, a day-shaped one survives no matter
// what it happens to sit on top of.
//
// Containment here means starting where the booking starts and finishing no
// later than its window. An event that starts alongside the booking but runs
// well past it is drawn rather than hidden, on purpose: an extra card costs her
// a moment working out what it is, where hiding a real commitment costs a
// double-booking, and only one of those is worth risking.
const isBookingEcho = (block, bookings) => {
  if (block.allDay || block.spansDays) return false;
  const from = minutesOfTime(block.startTime);
  const to = minutesOfTime(block.endTime);
  return bookings.some(booking => {
    const span = bookingCalendarSpan(booking);
    if (!span || span.date !== block.startDate) return false;
    return Math.abs(from - span.from) <= ECHO_START_TOLERANCE_MINUTES
      && to <= span.to + ECHO_START_TOLERANCE_MINUTES;
  });
};

// The `type` each edge function tags its sends with, in words Viktoria uses.
// Anything unmapped falls back to the raw type with the underscores taken out,
// so a new email template still reads sensibly here without a code change.
const EMAIL_TYPE_LABELS = {
  receipt: 'Booking receipt',
  admin_notif: 'Your own booking alert',
  instant_confirmation: 'Booking confirmation',
  instant_admin_notif: 'Your own booking alert',
  confirmation: 'Booking confirmation',
  rebook_confirmation: 'Rebooking confirmation',
  booking_moved: 'Moved appointment',
  reschedule_proposal: 'Reschedule suggestion',
  reschedule: 'Reschedule request',
  decline: 'Declined booking',
  cancellation: 'Cancellation',
  client_cancel_admin: 'Your own cancellation alert',
  reminder: 'Appointment reminder',
  order_confirmation: 'Shop order confirmation',
  order_admin: 'Your own order alert',
  order_response: 'Order reply',
  voucher_confirmation: 'Gift voucher confirmation',
  voucher_admin: 'Your own voucher alert',
  voucher_ready: 'Voucher ready',
  voucher_issued: 'Gift voucher',
  blast: 'Marketing email',
};

const describeEmailType = (type) =>
  EMAIL_TYPE_LABELS[type] || (type ? String(type).replace(/_/g, ' ') : 'Email');

// "3 Aug, 9:05am" — enough to tell "just now" from "last week" at a glance.
const formatFailureTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso)
      .toLocaleString('en-NZ', {
        day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true
      })
      .replace(/,/g, '');
  } catch {
    return String(iso);
  }
};

const AdminDashboard = ({ onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [todaysBookings, setTodaysBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Lands on Schedule → Today. Nothing creates pending bookings any more, so the
  // old default opened every morning on "All caught up! No pending booking
  // requests" — a screen that can never say anything else. Today is the one she
  // actually needs first.
  const [tab, setTab] = useState('today');
  const [section, setSection] = useState('schedule');

  const switchSection = (key) => {
    setSection(key);
    const defaults = { actions: 'pending', schedule: 'today', clients: 'clients', availability: 'availability', shop: 'addto', marketing: 'promos' };
    setTab(defaults[key]);
  };

  // Approval modal state
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    booking: null,
    selectedTime: '',
    clientType: 'new'
  });

  // Move modal state. She picks a real free slot and the booking goes there —
  // there is no proposal and nothing for the client to accept. The note is
  // optional; the new day and time do the talking on their own.
  const [moveModal, setMoveModal] = useState({
    isOpen: false,
    booking: null,
    note: ''
  });
  const [moveSlots, setMoveSlots] = useState([]);
  const [moveSlotsState, setMoveSlotsState] = useState('idle'); // idle | loading | ready | error
  const [moveSlotsError, setMoveSlotsError] = useState('');
  const [moveSlot, setMoveSlot] = useState(null);
  const [moveOpenDay, setMoveOpenDay] = useState('');
  const [moveReloadKey, setMoveReloadKey] = useState(0);
  const [movePageStart, setMovePageStart] = useState(() => new Date());
  const [moveNotice, setMoveNotice] = useState('');

  // Decline modal state
  const [declineModal, setDeclineModal] = useState({
    isOpen: false,
    booking: null,
    message: ''
  });

  // ── Shop state ──
  const [orders, setOrders] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);

  // ── Vouchers state ──
  const [vouchers, setVouchers] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);

  // Redeeming asks her to type the code rather than tap the row she is already
  // looking at. That was asked for: money comes off a voucher and there is no
  // undo, and a mis-tap on a phone would take it off the wrong one. Typing is
  // slow on purpose, and it also means the client actually has the voucher in
  // hand.
  //
  // `amount` is blank by default and blank means "use up whatever is left" —
  // the usual case, and one she should never have to type a number for. A
  // number in the box takes exactly that much and leaves the rest live.
  const [redeemModal, setRedeemModal] = useState({
    isOpen: false, code: '', amount: '', note: '', error: '',
  });

  const [orderModal, setOrderModal] = useState({ isOpen: false, order: null, message: '' });

  // ── Stock tab state ──
  const [stockProducts, setStockProducts] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockEdits, setStockEdits] = useState({}); // { [productId]: inputValue }

  // ── One-action-at-a-time guard ──
  // Every write below either emails a client or writes a Google Calendar event,
  // and nothing downstream is idempotent — a second calendar event silently eats
  // a bookable slot, because the availability engine reads Google's free/busy
  // back. `actionBusy` holds a key naming the action in flight (null when idle)
  // so the button that was pressed can say so while every other one locks. The
  // ref is what actually blocks: a second tap can be dispatched before React has
  // re-rendered with the new state, and a ref is updated the instant we set it.
  const [actionBusy, setActionBusy] = useState(null);
  const actionBusyRef = useRef(false);
  const isBusy = actionBusy !== null;

  const beginAction = (key) => {
    if (actionBusyRef.current) return false;
    actionBusyRef.current = true;
    setActionBusy(key);
    return true;
  };

  const endAction = () => {
    actionBusyRef.current = false;
    setActionBusy(null);
  };

  // ── Email delivery failures ──
  // Every email in the system goes through one helper, so a bad provider key
  // takes down confirmations, reminders and cancellations at once while
  // bookings carry on succeeding. Nothing else in the app would show it.
  const [emailFailures, setEmailFailures] = useState([]);
  const [failuresDismissing, setFailuresDismissing] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({ visible: false, type: 'success', message: '' });
  const showToast = (type, message) => setToast({ visible: true, type, message });
  const closeToast = () => setToast(prev => ({ ...prev, visible: false }));

  // ── Pinned notices ──
  // Toast gives every message five seconds and then takes it away. That is right
  // for "done"; it is not right for "the client was never told" or "it is not in
  // your calendar" — she reads this on a phone between clients and will not
  // always be looking when it appears. Anything with work still to do in it is
  // pinned to the top of the dashboard as well as toasted, and stays there until
  // she taps it away. Toast.js is shared with the client-facing pages, so this
  // is done here rather than by changing the timeout for everyone.
  const [notices, setNotices] = useState([]);
  const noticeIdRef = useRef(0);

  const pinNotice = (type, message) => {
    noticeIdRef.current += 1;
    const id = noticeIdRef.current;
    setNotices(prev => [{ id, type, message, at: new Date().toISOString() }, ...prev]);
    showToast(type, message);
  };

  const dismissNotice = (id) => setNotices(prev => prev.filter(n => n.id !== id));

  const [expandedCats, setExpandedCats] = useState({});
  const toggleCat = (key) => setExpandedCats(prev => ({ ...prev, [key]: !prev[key] }));
  const [productSearch, setProductSearch] = useState('');

  // ── Email blast state ──
  const [emailBlast, setEmailBlast] = useState({ subject: '', body: '', imageUrl: '', audience: 'all', preview: false, sending: false });
  const [customerEmails, setCustomerEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);

  // ── Stats state ──
  const [statsData, setStatsData] = useState({ bookings: [], orders: [] });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsMonth, setStatsMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [summersetPercent, setSummersetPercent] = useState('');

  // ── Approved tab month filter ──
  const [approvedMonth, setApprovedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // ── Blocked-out time on the Schedule tabs ──
  // Blocking a day writes to time_off / schedule_exceptions and the availability
  // engine honours it straight away — but this screen never read either table,
  // so a blocked day and a genuinely free one looked identical here. From the
  // chair that reads as "the block did not work".
  //
  // Held with the span they were fetched for: Today and Approved ask for
  // different ranges, and a stale month must not be drawn against today.
  //
  // Google Calendar is a third source alongside those two tables, and it fails
  // in its own ways, so it carries its own error rather than sharing theirs —
  // one being unreachable must not blank the others.
  const [scheduleBlocks, setScheduleBlocks] = useState({
    key: '', timeOff: [], exceptions: [], google: [], error: '', googleError: ''
  });

  // What a client would actually be offered today, and why.
  //
  // The block cards below answer "what is in the way", which is only a third of
  // the question. They never read business_hours, never mention the minimum
  // notice, and never show the booking window — so a Saturday she is closed on
  // drew "Enjoy your day off!", identical to a wide-open Wednesday, and a day
  // that was unbookable purely by policy looked like a day she was free. When
  // she said the site was showing her booked while she was free, this screen
  // could neither confirm nor refute it, because it was answering a different
  // question from the one the booking form answers.
  //
  // Sampled at 60 minutes. Her treatments vary, so this is a representative
  // figure rather than an exact one, and the wording says so.
  const [quickBookOpen, setQuickBookOpen] = useState(false);

  const [bookability, setBookability] = useState({
    key: '', loading: false, hours: null, settings: null,
    offered: 0, filtered: 0, lastBookableDate: null, error: '',
  });

  // Bumped every time the bookings land, purely to re-run the block fetch above.
  // The two have to move together: the Google list is filtered against the
  // bookings, so a booking that moved or was cancelled leaves its old calendar
  // event unmatched, and it then draws as a block card — at the old time, under
  // the client's name, telling her to delete an event that is no longer there.
  const [bookingsVersion, setBookingsVersion] = useState(0);

  // ── Promotions state ──
  const [promotions, setPromotions] = useState([]);
  const [promosLoading, setPromosLoading] = useState(false);
  const [promoModal, setPromoModal] = useState({
    isOpen: false,
    editing: null,
    form: { title: '', message: '', is_active: false, bg_color: '#2a4e3a', type: 'banner', image_url: '' }
  });
  const [productModal, setProductModal] = useState({
    isOpen: false,
    editing: null,
    form: { name: '', category: '', description: '', price: '', image_url: '', display_order: 0, is_active: true }
  });

  // ── Banner image upload state ──
  const [bannerImages, setBannerImages] = useState([]);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [showBannerGallery, setShowBannerGallery] = useState(false);
  // ── Email blast image upload state ──
  const [emailImageUploading, setEmailImageUploading] = useState(false);
  const [showEmailGallery, setShowEmailGallery] = useState(false);
  // ── Product image upload state ──
  const [productImageUploading, setProductImageUploading] = useState(false);
  const [showProductGallery, setShowProductGallery] = useState(false);

  const loadShopData = useCallback(async () => {
    setShopLoading(true);
    try {
      const [ordersData, productsData] = await Promise.all([
        supabaseService.getShopOrders(),
        supabaseService.getAllShopProducts(),
      ]);
      setOrders(ordersData || []);
      setShopProducts(productsData || []);
    } catch (err) {
      console.error('Error loading shop data:', err);
    } finally {
      setShopLoading(false);
    }
  }, []);

  const loadStockData = useCallback(async () => {
    setStockLoading(true);
    try {
      const data = await supabaseService.getAllShopProducts();
      setStockProducts(data || []);
    } catch (err) {
      console.error('Error loading stock data:', err);
    } finally {
      setStockLoading(false);
    }
  }, []);

  const loadPromosData = useCallback(async () => {
    setPromosLoading(true);
    try {
      const data = await supabaseService.getAllPromotions();
      setPromotions(data || []);
    } catch (err) {
      console.error('Error loading promotions:', err);
    } finally {
      setPromosLoading(false);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await supabaseService.getBookings();
      setBookings(data);

      const todayData = await supabaseService.getTodaysBookings();
      setTodaysBookings(todayData);
      // Only once both lists are in state, so the refetched blocks are filtered
      // against the bookings that are actually on screen.
      setBookingsVersion(v => v + 1);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Diagnostics, not core data — if this table can't be read the dashboard
  // carries on exactly as before, minus the warning.
  useEffect(() => {
    let cancelled = false;
    supabaseService.getEmailFailures()
      .then(rows => { if (!cancelled) setEmailFailures(rows || []); })
      .catch(err => console.error('Could not load email failures:', err));
    return () => { cancelled = true; };
  }, []);

  // Acknowledging is a note to herself that she's seen it — it doesn't retry
  // anything, and the banner comes straight back if another email fails.
  const handleDismissEmailFailures = async () => {
    if (failuresDismissing) return;
    setFailuresDismissing(true);
    try {
      await supabaseService.acknowledgeEmailFailures();
      setEmailFailures([]);
      showToast('success', 'Marked as seen. It will come back if another email fails.');
    } catch (err) {
      console.error('Could not acknowledge email failures:', err);
      showToast('error', 'Could not clear that warning. Please try again.');
    } finally {
      setFailuresDismissing(false);
    }
  };

  useEffect(() => {
    const unsubscribe = supabaseService.subscribeToBookings(() => {
      loadBookings();
    });
    return unsubscribe;
  }, [loadBookings]);

  useEffect(() => {
    const unsubscribe = supabaseService.subscribeToOrders(() => {
      loadShopData();
    });
    return unsubscribe;
  }, [loadShopData]);

  // Which dates the Schedule tab is currently showing. Today is one day; the
  // Approved tab is whichever month is in its picker, and that month can already
  // have gone — which is why this reads the range-scoped service methods rather
  // than the from-today-on ones the Availability panel uses.
  const blocksRange = useMemo(() => {
    if (tab === 'today') {
      const today = toISODate(new Date());
      return { start: today, end: today };
    }
    if (tab === 'approved') {
      const [year, month] = approvedMonth.split('-').map(Number);
      if (!year || !month) return null;
      const lastDay = new Date(year, month, 0).getDate();
      const mm = String(month).padStart(2, '0');
      return { start: `${year}-${mm}-01`, end: `${year}-${mm}-${String(lastDay).padStart(2, '0')}` };
    }
    return null;
  }, [tab, approvedMonth]);

  useEffect(() => {
    if (!blocksRange) return undefined;
    const key = rangeKey(blocksRange);
    let cancelled = false;

    // Google answers separately from the two tables, and separately is how it
    // has to fail: her diary being unreachable is not a reason to stop showing
    // the time off she entered here. So its rejection is caught on its own and
    // carried as a message instead of taking the whole load down.
    const googleBusy = supabaseService
      .getBusyPeriods({ startDate: blocksRange.start, endDate: blocksRange.end })
      .then(busy => ({ busy: busy || [], googleError: '' }))
      .catch(err => {
        console.error('Error loading Google Calendar busy periods:', err);
        return { busy: [], googleError: err.message || 'Could not check your Google Calendar.' };
      });

    Promise.all([
      supabaseService.getTimeOffInRange(blocksRange.start, blocksRange.end),
      supabaseService.getScheduleExceptionsInRange(blocksRange.start, blocksRange.end),
      googleBusy,
    ])
      .then(([off, exceptions, google]) => {
        if (cancelled) return;
        setScheduleBlocks({
          key,
          timeOff: off || [],
          exceptions: exceptions || [],
          google: google.busy
            .map((period, i) => googleBlockFromPeriod(period, i))
            .filter(Boolean),
          error: '',
          googleError: google.googleError,
        });
      })
      .catch(err => {
        console.error('Error loading blocked-out time:', err);
        if (cancelled) return;
        // Said out loud rather than swallowed. A silent failure here looks
        // exactly like "nothing is blocked", which is the bug being fixed.
        setScheduleBlocks({
          key,
          timeOff: [],
          exceptions: [],
          google: [],
          error: 'Could not load your blocked-out time, so anything you have blocked is missing from this list.',
          googleError: '',
        });
      });

    return () => { cancelled = true; };
    // bookingsVersion is a trigger, not an input: the blocks are filtered against
    // the bookings downstream, so they have to be refetched whenever those change
    // or the filter is comparing this list against a set it no longer describes.
    // The key is unchanged on such a refetch, so nothing flickers back to loading.
  }, [blocksRange, bookingsVersion]);

  // Asks the booking form's own question, through the same endpoint the booking
  // form uses, so this panel and the public site can never disagree about
  // whether a day is bookable. Deliberately NOT getAdminSlots: that one ignores
  // the minimum notice, which is exactly one of the things she needs to see.
  useEffect(() => {
    if (tab !== 'today') return undefined;
    const today = toISODate(new Date());
    let cancelled = false;

    setBookability(prev => ({ ...prev, loading: true, error: '' }));

    Promise.all([
      supabaseService.getBusinessHours(),
      supabaseService.getBookingSettings(),
      supabaseService.getAvailableSlots({
        startDate: today,
        endDate: today,
        durationMinutes: 60,
      }),
    ])
      .then(([hours, settings, avail]) => {
        if (cancelled) return;
        setBookability({
          key: today,
          loading: false,
          hours: hours || [],
          settings: settings || null,
          offered: avail.slots.length,
          filtered: avail.filtered,
          lastBookableDate: avail.lastBookableDate,
          error: '',
        });
      })
      .catch(err => {
        console.error('Could not work out what a client would be offered:', err);
        if (cancelled) return;
        // Said out loud, never guessed at. A silent failure here would read as
        // "nothing is bookable", which is the exact misreading this panel exists
        // to prevent.
        setBookability(prev => ({
          ...prev,
          loading: false,
          error: err.message || 'Could not check what the booking page is offering today.',
        }));
      });

    return () => { cancelled = true; };
  }, [tab, bookingsVersion]);

  const loadEmailData = useCallback(async (audience = 'all') => {
    setEmailsLoading(true);
    try {
      const emails = await supabaseService.getUniqueCustomerEmails(audience);
      setCustomerEmails(emails);
    } catch (err) {
      console.error('Error loading emails:', err);
    } finally {
      setEmailsLoading(false);
    }
  }, []);

  const loadStatsData = useCallback(async (month) => {
    setStatsLoading(true);
    try {
      const [year, m] = month.split('-').map(Number);
      const startDate = `${year}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(year, m, 0).getDate();
      const endDate = `${year}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const [bookings, orders] = await Promise.all([
        supabaseService.getBookingsForPeriod(startDate, endDate),
        supabaseService.getFulfilledOrdersForPeriod(startDate, endDate),
      ]);
      setStatsData({ bookings: bookings || [], orders: orders || [] });
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'orders' || tab === 'addto') {
      loadShopData();
    }
    if (tab === 'promos') {
      loadPromosData();
    }
    if (tab === 'stock') {
      loadStockData();
    }
    if (tab === 'email') {
      loadEmailData(emailBlast.audience);
    }
    if (tab === 'stats') {
      loadStatsData(statsMonth);
    }
    if (tab === 'vouchers') {
      setVouchersLoading(true);
      supabaseService.getGiftVouchers()
        .then(setVouchers)
        .catch(err => console.error('Error loading vouchers:', err))
        .finally(() => setVouchersLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, loadShopData, loadPromosData, loadStockData, loadEmailData, loadStatsData, emailBlast.audience, statsMonth]);

  // ==================== ORDER RESPONSE ====================

  const openOrderModal = (order) => {
    const itemList = (order.shop_order_items || []).map(i => `• ${i.product_name} x${i.quantity} — ${i.price}`).join('\n');
    const defaultMessage = `Hi ${order.customer_name}!\n\nThank you for your order. I've received the following:\n\n${itemList}\n\nI'll be in touch shortly to arrange ${order.fulfillment === 'pickup' ? 'pickup' : 'delivery'}.\n\nThanks,\nViktoria`;
    setOrderModal({ isOpen: true, order, message: defaultMessage });
  };

  const closeOrderModal = () => setOrderModal({ isOpen: false, order: null, message: '' });

  const handleOrderResponse = async () => {
    if (!beginAction('order-response')) return;
    try {
      if (!orderModal.message.trim()) { alert('Please write a message'); return; }
      await supabaseService.updateShopOrderStatus(orderModal.order.id, 'responded', orderModal.message);
      supabaseService.sendNotification('order_response', {
        orderId: orderModal.order.id,
        customer_name: orderModal.order.customer_name,
        email: orderModal.order.email,
        responseMessage: orderModal.message,
        items: orderModal.order.shop_order_items || [],
      }).catch(err => console.error('Order response email failed:', err));
      closeOrderModal();
      loadShopData();
    } catch (err) {
      console.error('Error responding to order:', err);
      alert('Failed to send response. Please try again.');
    } finally {
      endAction();
    }
  };

  const handleMarkFulfilled = async (order) => {
    if (!beginAction(`fulfil:${order.id}`)) return;
    try {
      if (!window.confirm(`Mark order for ${order.customer_name} as fulfilled?`)) return;
      await supabaseService.updateShopOrderStatus(order.id, 'fulfilled');
      // Deduct stock for each item — runs in background, refreshes stock tab if open
      const items = (order.shop_order_items || []).map(i => ({
        product_id: i.product_id,
        quantity: i.quantity,
      }));
      await supabaseService.deductStockForOrder(items);
      loadShopData();
      // Refresh stock list too so badge counts stay accurate
      if (tab === 'stock') loadStockData();
    } catch (err) {
      console.error('Failed to mark fulfilled:', err);
      alert('Failed to update order status.');
    } finally {
      endAction();
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!beginAction(`delete-order:${order.id}`)) return;
    try {
      if (!window.confirm(`Delete order from ${order.customer_name}? This cannot be undone.`)) return;
      await supabaseService.deleteShopOrder(order.id);
      setOrders(prev => prev.filter(o => o.id !== order.id));
    } catch (err) {
      console.error('Failed to delete order:', err);
      alert('Failed to delete order.');
    } finally {
      endAction();
    }
  };

  // ==================== VOUCHER MANAGEMENT ====================

  // The one payload the voucher email is ever sent with, so "Payment received"
  // and "Re-send" cannot drift apart and mint two different-looking emails for
  // the same voucher.
  //
  // balance_cents rides along with amount because re-send is offered on
  // part-used rows, and that is exactly when someone asks for the email again.
  // Without it the certificate prints what the voucher was bought for — she
  // emails a $200 card for a code with $115 on it and has to refuse $85 of her
  // own document at the door. amount is dollars and balance_cents is cents;
  // both travel as they are stored and the email does the reading.
  const voucherEmailPayload = (voucher) => ({
    purchaser_name: voucher.purchaser_name,
    purchaser_email: voucher.purchaser_email,
    recipient_name: voucher.recipient_name,
    amount: voucher.amount,
    balance_cents: voucher.balance_cents,
    code: voucher.code,
    expires_at: voucher.expires_at,
    issued_at: voucher.issued_at,
  });

  // Sends the voucher email and returns whether it actually went. Never throws:
  // by the time this runs the voucher exists and is spendable, so a failure here
  // is news to deliver, not a reason to unwind anything.
  //
  // The return value is the whole point. A try/catch around the send catches
  // nothing — sendNotification swallows an invoke that never lands and answers
  // null, and the edge function answers 200 with success:true even when the
  // provider refuses the send. The per-recipient ok flag is the only honest
  // signal there is, same test as the booking-moved path above.
  const sendVoucherEmail = async (voucher) => {
    try {
      const result = await supabaseService.sendNotification('voucher_issued', voucherEmailPayload(voucher));
      return !!result?.emailResults?.[0]?.ok;
    } catch (err) {
      console.error('Voucher email failed:', err);
      return false;
    }
  };

  // Legacy only. Rows still sitting on the old hand-run lifecycle ('ready',
  // waiting to be picked up) need a way to be closed off, and they have no code
  // to redeem because none was ever issued. Nothing new arrives in this state —
  // a paid request now goes straight to 'issued' with a code on it.
  const handleVoucherStatus = async (voucher, newStatus) => {
    if (!beginAction(`voucher:${voucher.id}`)) return;
    try {
      await supabaseService.updateGiftVoucherStatus(voucher.id, newStatus);
      setVouchers(prev => prev.map(v => v.id === voucher.id ? { ...v, status: newStatus } : v));
      showToast('success', 'Voucher marked as collected.');
    } catch (err) {
      console.error('Failed to update voucher status:', err);
      showToast('error', 'Failed to update voucher status.');
    } finally {
      endAction();
    }
  };

  // "Payment received": the bank transfer has landed, so the voucher becomes
  // real — code, issue date, three-year expiry — and gets emailed to whoever
  // bought it. The RPC is idempotent, so a double tap returns the same voucher
  // rather than minting a second code; already_issued tells us which happened
  // so we don't email the same person the same voucher twice.
  const handleIssueVoucher = async (voucher) => {
    if (!beginAction(`voucher:${voucher.id}`)) return;
    try {
      const issued = await supabaseService.issueGiftVoucher(voucher.id);

      // balance_cents is the full face value at this point — issuing is what
      // puts money on the code. Carried over rather than dropped: the row she
      // is looking at goes on to feed "Re-send voucher email", and a row
      // holding nothing there is a row that cannot say what is left on it.
      const minted = {
        status: issued.status,
        code: issued.code,
        issued_at: issued.issued_at,
        expires_at: issued.expires_at,
        balance_cents: issued.balance_cents ?? voucher.balance_cents ?? null,
      };

      setVouchers(prev => prev.map(v => v.id === voucher.id ? { ...v, ...minted } : v));

      if (issued.already_issued) {
        // Nothing was minted and, deliberately, nothing was emailed — sending
        // again from here would put a second copy of the same voucher in their
        // inbox. Where this is reached from is a first press whose answer never
        // came back (wifi drops, phone locks), so she genuinely does not know
        // whether the email went. Said plainly, and pinned, because the next
        // thing she does depends on the answer.
        pinNotice('warning', `${voucher.purchaser_name}'s voucher was already made up, so I have not emailed anything just now. The code is ${issued.code}. If they never got it, use "Re-send voucher email" on their card.`);
        return;
      }

      // Awaited, not fire-and-forget: this is the email that IS the voucher. If
      // it fails she has to know now, while the person is still on the phone to
      // her — a code sitting in the database that nobody received is worse than
      // no code at all.
      if (await sendVoucherEmail({ ...voucher, ...minted })) {
        showToast('success', `Voucher ${issued.code} issued and emailed to ${voucher.purchaser_name}.`);
      } else {
        // Pinned, not toasted. The voucher is real and spendable either way, so
        // the message has to lead with that — if it reads like the whole thing
        // failed she presses Payment received again, and the second press is
        // the one that teaches her the button does nothing.
        pinNotice('error', `Voucher ${issued.code} IS issued and valid — but the email to ${voucher.purchaser_email} did not go. Don't press Payment received again; use "Re-send voucher email" on ${voucher.purchaser_name}'s card.`);
      }
    } catch (err) {
      console.error('Failed to issue voucher:', err);
      // The RPC's messages are written for her ("That request was declined.
      // Reopen it before issuing a voucher."), so show them as they are.
      showToast('error', err.message || 'Failed to issue that voucher.');
    } finally {
      endAction();
    }
  };

  // The recovery path. "Payment received" only exists while the row is pending,
  // so once a voucher is issued that button is gone for good — and without this
  // a lost email left her reading the code off the card and writing the whole
  // thing by hand. The row already carries everything the email needs, so this
  // is the same send again, nothing minted and nothing changed in the database.
  const handleResendVoucherEmail = async (voucher) => {
    if (!beginAction(`voucher:${voucher.id}`)) return;
    try {
      if (await sendVoucherEmail(voucher)) {
        showToast('success', `Voucher ${voucher.code} emailed to ${voucher.purchaser_email} again.`);
      } else {
        pinNotice('error', `Voucher ${voucher.code} still did not reach ${voucher.purchaser_email}. The voucher itself is fine — it's the email that keeps failing, so check the address is right, or send them the code and the link yourself.`);
      }
    } catch (err) {
      // sendVoucherEmail never throws, but showToast and pinNotice are inside
      // this try and a throw from either would otherwise be an unhandled
      // rejection: no message, and the busy guard released with her none the
      // wiser about whether the email went.
      console.error('Failed to re-send voucher email:', err);
      pinNotice('error', `Something went wrong re-sending voucher ${voucher.code}. I can't tell you whether it reached ${voucher.purchaser_email}, so check with them before you send it again.`);
    } finally {
      endAction();
    }
  };

  // No voucher argument, on purpose. This used to open from a row and print that
  // row's details at the top while the database acted on whatever code she then
  // typed — so a slip could mark a different voucher used behind a header
  // confidently naming another one. The modal now claims nothing it doesn't know.
  const openRedeemModal = () => {
    if (isBusy) return;
    setRedeemModal({ isOpen: true, code: '', amount: '', note: '', error: '' });
  };

  const closeRedeemModal = () => {
    if (actionBusyRef.current) return;
    setRedeemModal({ isOpen: false, code: '', amount: '', note: '', error: '' });
  };

  const handleRedeemVoucher = async () => {
    // Validated before the guard is taken, so a typo in the amount leaves the
    // form exactly as it was and she can just fix the number.
    const amountCents = dollarsToCents(redeemModal.amount);
    if (Number.isNaN(amountCents)) {
      setRedeemModal(prev => ({ ...prev, error: 'Type the amount in dollars, like 45 or 45.50. Leave it empty to use up the whole voucher.' }));
      return;
    }

    if (!beginAction('voucher-redeem')) return;
    setRedeemModal(prev => ({ ...prev, error: '' }));
    try {
      const result = await supabaseService.redeemGiftVoucher(redeemModal.code, amountCents, redeemModal.note);

      const fullyUsed = !!result.fully_used;
      const who = result.recipient_name || result.purchaser_name;

      // What actually came off, and what is still there. Both are read back to
      // her in words rather than left for her to work out from a status: a
      // voucher that still has money on it is the whole point of this change,
      // and "used" on its own would be the wrong thing to take away.
      // Every number is derived from the cents the database sent back; the
      // typed amount is only a fallback if the RPC ever stops saying.
      const wentOn = formatCents(result.spent_cents) || formatAmount(redeemModal.amount.trim());
      const left = formatCents(result.balance_cents);
      const wentOnPhrase = wentOn ? `$${wentOn} went on ${who}'s treatment` : `it went on ${who}'s treatment`;
      const leftPhrase = left ? `$${left} is still on it` : 'there is still money on it';

      // Matched on what came back rather than on the row the modal was opened
      // from. She types the code, and the code is what the database acted on —
      // if she typed a different one, that is the voucher that was used.
      setVouchers(prev => prev.map(v => (
        (result.id && v.id === result.id) || (v.code && v.code === result.code)
          ? {
              ...v,
              status: result.status || (fullyUsed ? 'redeemed' : 'part_used'),
              balance_cents: result.balance_cents,
              // redeemed_at marks the moment the last of it went, so it only
              // gets stamped on a full spend; the RPC does the same. The note
              // is the latest one, blank included, again matching the row.
              redeemed_at: fullyUsed ? (result.redeemed_at || new Date().toISOString()) : v.redeemed_at,
              redeemed_note: redeemModal.note.trim() || null,
            }
          : v
      )));

      setRedeemModal({ isOpen: false, code: '', amount: '', note: '', error: '' });

      if (fullyUsed) {
        showToast('success', `${result.code} is all used up. ${wentOnPhrase[0].toUpperCase()}${wentOnPhrase.slice(1)}, and that was the last of it.`);
      } else {
        // Pinned, not toasted. There is still money on this one and she is
        // usually about to say so out loud to the person in front of her, so a
        // five second toast is not where a live balance belongs.
        pinNotice('warning', `${result.code}: ${wentOnPhrase} and ${leftPhrase}. Same code next time, good until ${formatISODate(result.expires_at)}.`);
      }

      if (result.was_expired) {
        // The RPC deliberately does not refuse an expired voucher — whether to
        // honour one that has just run out is her call, not the system's. She
        // still needs telling that it happened, and a toast she might not be
        // looking at is not enough for something she may want to unpick.
        // Pinned last so this is the one the toast is showing.
        pinNotice('error', `${result.code} had expired on ${formatISODate(result.expires_at)} and was still marked as used. If you did not mean to honour it, let me know.`);
      }
    } catch (err) {
      console.error('Failed to redeem voucher:', err);
      // Kept in the modal rather than toasted away: these messages tell her
      // what to do next ("That voucher was already used on 18 Aug 2026") and
      // she is standing in front of the person holding it.
      setRedeemModal(prev => ({ ...prev, error: err.message || 'Could not mark that voucher as used.' }));
    } finally {
      endAction();
    }
  };

  // ==================== PRODUCT MANAGEMENT ====================

  const existingCategories = [...new Set(shopProducts.map(p => p.category))].filter(Boolean).sort();

  const openAddProduct = () => {
    setProductModal({
      isOpen: true,
      editing: null,
      form: { name: '', category: existingCategories[0] || '', description: '', price: '', image_url: '', display_order: 0, is_active: true }
    });
  };

  const openEditProduct = (product) => {
    setProductModal({
      isOpen: true,
      editing: product,
      form: { name: product.name, category: product.category, description: product.description || '', price: product.price, image_url: product.image_url || '', display_order: product.display_order, is_active: product.is_active }
    });
  };

  const closeProductModal = () => { setProductModal(prev => ({ ...prev, isOpen: false, editing: null })); setShowProductGallery(false); };

  const handleSaveProduct = async () => {
    const f = productModal.form;
    if (!f.name || !f.price || !f.category || f.category === '__new__') { alert('Name, category and price are required.'); return; }
    try {
      if (productModal.editing) {
        await supabaseService.updateShopProduct(productModal.editing.id, f);
      } else {
        await supabaseService.createShopProduct(f);
      }
      closeProductModal();
      loadShopData();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Failed to save product. Please try again.');
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      await supabaseService.deleteShopProduct(product.id);
      loadShopData();
    } catch (err) {
      alert('Failed to delete product.');
    }
  };

  // ==================== PROMOTIONS MANAGEMENT ====================

  const openAddPromo = () => {
    setPromoModal({
      isOpen: true,
      editing: null,
      form: { title: '', message: '', is_active: false, bg_color: '#2a4e3a', type: 'banner', image_url: '' }
    });
  };

  const openEditPromo = (promo) => {
    setPromoModal({
      isOpen: true,
      editing: promo,
      form: { title: promo.title, message: promo.message, is_active: promo.is_active, bg_color: promo.bg_color || '#2a4e3a', type: promo.type || 'banner', image_url: promo.image_url || '' }
    });
  };

  const closePromoModal = () => {
    setPromoModal(prev => ({ ...prev, isOpen: false, editing: null }));
    setShowBannerGallery(false);
  };

  const loadBannerImages = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('banner-images')
        .list('', { sortBy: { column: 'created_at', order: 'desc' } });
      if (error) throw error;
      const imgs = (data || [])
        .filter(f => f.name !== '.emptyFolderPlaceholder')
        .map(f => ({
          name: f.name,
          url: supabase.storage.from('banner-images').getPublicUrl(f.name).data.publicUrl
        }));
      setBannerImages(imgs);
    } catch (err) {
      console.error('Failed to load banner images:', err);
    }
  };

  const uploadBannerImage = async (file) => {
    setBannerUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `banner-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('banner-images')
        .upload(fileName, file, { upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('banner-images').getPublicUrl(fileName);
      setPromoModal(prev => ({ ...prev, form: { ...prev.form, image_url: data.publicUrl } }));
      await loadBannerImages();
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setBannerUploading(false);
    }
  };

  const uploadEmailImage = async (file) => {
    setEmailImageUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `email-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('banner-images')
        .upload(fileName, file, { upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('banner-images').getPublicUrl(fileName);
      setEmailBlast(prev => ({ ...prev, imageUrl: data.publicUrl }));
      await loadBannerImages();
    } catch (err) {
      console.error('Failed to upload email image:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setEmailImageUploading(false);
    }
  };

  const uploadProductImage = async (file) => {
    setProductImageUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `product-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('banner-images')
        .upload(fileName, file, { upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('banner-images').getPublicUrl(fileName);
      setProductModal(prev => ({ ...prev, form: { ...prev.form, image_url: data.publicUrl } }));
      await loadBannerImages();
    } catch (err) {
      console.error('Failed to upload product image:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setProductImageUploading(false);
    }
  };

  const handleSavePromo = async () => {
    const f = promoModal.form;
    if (!f.title || !f.message) { alert('Title and message are required.'); return; }
    try {
      if (promoModal.editing) {
        await supabaseService.updatePromotion(promoModal.editing.id, f);
      } else {
        await supabaseService.createPromotion(f);
      }
      closePromoModal();
      loadPromosData();
    } catch (err) {
      console.error('Error saving promotion:', err);
      alert('Failed to save promotion. Please try again.');
    }
  };

  const handleTogglePromo = async (promo) => {
    try {
      await supabaseService.updatePromotion(promo.id, { is_active: !promo.is_active });
      loadPromosData();
    } catch (err) {
      alert('Failed to update promotion.');
    }
  };

  const handleDeletePromo = async (promo) => {
    if (!window.confirm(`Delete promotion "${promo.title}"? This cannot be undone.`)) return;
    try {
      await supabaseService.deletePromotion(promo.id);
      loadPromosData();
    } catch (err) {
      alert('Failed to delete promotion.');
    }
  };

  // ==================== APPROVAL ====================

  const openApprovalModal = (booking) => {
    setApprovalModal({
      isOpen: true,
      booking,
      selectedTime: booking.time_range_start || '',
      clientType: booking.client_type || 'returning'
    });
  };

  const closeApprovalModal = () => {
    setApprovalModal({ isOpen: false, booking: null, selectedTime: '', clientType: 'returning' });
  };

  const handleApproveWithTime = async () => {
    if (!beginAction('approve')) return;

    try {
      if (!approvalModal.selectedTime) {
        alert('Please select a time for the appointment');
        return;
      }

      await supabaseService.updateBookingStatus(
        approvalModal.booking.id,
        'approved',
        {
          finalTime: approvalModal.selectedTime,
          clientType: approvalModal.clientType
        }
      );

      // Send confirmation email + create calendar event
      const booking = approvalModal.booking;
      const treatments = booking.booking_treatments || [];
      const treatmentNames = treatments.map(bt => bt.treatment_name).join(', ') || 'Appointment';

      closeApprovalModal();
      setBookings(prev => prev.map(b =>
        b.id === booking.id
          ? { ...b, status: 'approved', time: approvalModal.selectedTime, client_type: approvalModal.clientType }
          : b
      ));

      const result = await supabaseService.sendNotification('approved', {
        name: booking.name,
        email: booking.email,
        phone: booking.phone,
        date: booking.date,
        time: approvalModal.selectedTime,
        clientType: approvalModal.clientType,
        treatmentNames,
        total_duration: booking.total_duration,
        total_price: booking.total_price,
        notes: booking.notes
      });

      // Store the calendar event ID so cancelling can remove it from her diary later.
      if (result?.calendarEventId) {
        await supabaseService.updateBookingStatus(booking.id, 'approved', {
          finalTime: approvalModal.selectedTime,
          calendarEventId: result.calendarEventId
        }).catch(err => console.error('Failed to store calendar event ID:', err));
      }

      // Show contextual toast based on what succeeded. Anything that leaves her
      // with something to do is pinned as well as toasted — five seconds is not
      // long enough to catch "the client was never told".
      if (!result) {
        pinNotice('error', 'Booking approved but notifications failed. Check Supabase edge function logs.');
      } else if (result.emailResults?.[0]?.ok && result.calendarEventId) {
        showToast('success', 'Booking approved — confirmation email sent and added to your calendar.');
      } else if (result.emailResults?.[0]?.ok) {
        pinNotice('warning', 'Booking approved and email sent, but it did not reach your Google Calendar. Check the Google credentials in Supabase.');
      } else {
        pinNotice('warning', 'Booking approved, but the client email may not have been delivered. Contact them directly.');
      }

      loadBookings();
    } catch (err) {
      console.error('Error approving booking:', err);
      showToast('error', 'Failed to approve booking. Please try again.');
    } finally {
      endAction();
    }
  };

  // ==================== MOVE A CONFIRMED BOOKING ====================
  // She picks a real free slot and the appointment goes there. No proposal, no
  // limbo, nothing to wait on: the booking has moved by the time the modal
  // closes, and the client is told where to. The note is optional and goes into
  // that email in her own words.

  const openMoveModal = (booking) => {
    setMoveSlot(null);
    setMoveSlots([]);
    setMoveSlotsState('idle');
    setMoveSlotsError('');
    setMoveOpenDay('');
    setMovePageStart(new Date());
    setMoveNotice('');
    setMoveModal({ isOpen: true, booking, note: '' });
  };

  const closeMoveModal = () => {
    setMoveModal({ isOpen: false, booking: null, note: '' });
    setMoveSlot(null);
    setMoveSlots([]);
    setMoveSlotsState('idle');
    setMoveSlotsError('');
    setMoveOpenDay('');
    setMoveNotice('');
  };

  // Paging never goes back past today — there is nothing to move anyone into in
  // the past.
  const shiftMovePage = (deltaDays) => {
    const next = addDays(movePageStart, deltaDays);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setMovePageStart(next < today ? today : next);
    setMoveOpenDay('');
  };

  // The appointment has to still fit, so the picker is sized by the booking's own
  // length. Older bookings predate total_duration; an hour is the safe guess.
  const moveBookingId = moveModal.booking?.id || null;
  const moveDuration = Number(moveModal.booking?.total_duration) || 60;
  const moveRest = Number(moveModal.booking?.rest_minutes) || 0;
  const moveSending = actionBusy === 'move';

  useEffect(() => {
    if (!moveModal.isOpen) return undefined;

    let cancelled = false;
    setMoveSlotsState('loading');
    setMoveSlotsError('');

    supabaseService
      .getAdminSlots({
        startDate: toISODate(movePageStart),
        endDate: toISODate(addDays(movePageStart, MOVE_DAYS_PER_PAGE - 1)),
        durationMinutes: moveDuration,
        // The booking carries its own rest; moving it must not lose that.
        restMinutes: moveRest,
        // So the booking being moved does not count itself as busy — otherwise
        // shifting a 10:00 appointment to 10:15 is never offered.
        excludeBookingId: moveBookingId || null,
      })
      .then(rows => {
        if (cancelled) return;
        setMoveSlots(rows || []);
        setMoveSlotsState('ready');
      })
      .catch(err => {
        if (cancelled) return;
        setMoveSlots([]);
        setMoveSlotsError(err.message || 'Could not load your free times.');
        setMoveSlotsState('error');
      });

    return () => { cancelled = true; };
  }, [moveModal.isOpen, moveBookingId, moveDuration, moveRest, movePageStart, moveReloadKey]);

  const moveSlotsByDay = useMemo(
    () => moveSlots.reduce((acc, slot) => {
      (acc[slot.slot_date] = acc[slot.slot_date] || []).push(slot);
      return acc;
    }, {}),
    [moveSlots]
  );

  const moveDays = useMemo(
    () => Object.keys(moveSlotsByDay).sort(),
    [moveSlotsByDay]
  );

  const moveAtStart = toISODate(movePageStart) === toISODate(new Date());

  const handleMove = async () => {
    const booking = moveModal.booking;
    if (!booking || !moveSlot) return;
    if (!beginAction('move')) return;

    const newDate = moveSlot.slot_date;
    const newTime = moveSlot.slot_time;
    const note = moveModal.note.trim();

    try {
      // The RPC moves it and holds the new slot; the email and the calendar
      // event are done for us and reported back separately, because either can
      // fail after the move itself is already committed.
      const result = await supabaseService.moveBooking(booking.id, {
        date: newDate,
        time: newTime,
        note: note || null,
      });

      const who = booking.name;
      const when = `${formatDate(newDate)} at ${formatTime(newTime)}`;
      const wasWhen = result?.old_date
        ? `${formatDate(result.old_date)}${result.old_time ? ` at ${formatTime(result.old_time)}` : ''}`
        : `${formatDate(booking.date)}${booking.time ? ` at ${formatTime(booking.time)}` : ''}`;

      closeMoveModal();

      // The card belongs on another date now — move it before the reload lands
      // so it doesn't sit there under the old time for a beat. Today's list is
      // its own query, so a booking moved off today has to be dropped from it
      // by hand; one moved onto today arrives with the reload.
      setBookings(prev => prev.map(b =>
        b.id === booking.id
          ? { ...b, date: newDate, time: newTime, calendar_event_id: result?.calendarEventId || b.calendar_event_id }
          : b
      ));
      const stillToday = newDate === toISODate(new Date());
      setTodaysBookings(prev => prev
        .filter(b => b.id !== booking.id || stillToday)
        .map(b => (b.id === booking.id ? { ...b, date: newDate, time: newTime } : b)));

      if (result?.notified && result?.calendarEventId) {
        showToast('success', `${who} moved to ${when} — email sent and your calendar updated.`);
      } else if (result?.calendarEventId) {
        pinNotice('warning', `${who} is now booked for ${when} and it is in your calendar, but the email may not have been delivered — they may still be expecting ${wasWhen}. Tell them yourself.`);
      } else if (result?.notified) {
        pinNotice('warning', `${who} moved to ${when} and has been emailed, but it did not reach your Google Calendar. Add it by hand or it will not show up in your day.`);
      } else {
        pinNotice('warning', `${who} moved to ${when}, but the email may not have been delivered and it is not in your Google Calendar. They may still be expecting ${wasWhen} — check both.`);
      }

      loadBookings();
    } catch (err) {
      if (err.slotTaken) {
        // Still in the modal, still on the old time — she just picks again.
        setMoveSlot(null);
        setMoveReloadKey(k => k + 1);
        setMoveNotice('That time went while you were choosing. Nothing has changed — these are the times still free.');
        showToast('warning', 'That time has just gone. I have reloaded what is still free.');
      } else {
        console.error('Error moving booking:', err);
        showToast('error', err.message || 'Could not move that booking. Nothing has changed — please try again.');
      }
    } finally {
      endAction();
    }
  };

  // ==================== DECLINE ====================

  const openDeclineModal = (booking) => {
    const defaultMessage = `I apologise, but I'm unable to accommodate your requested appointment time. If you'd like to book for a different date, please feel free to submit a new request.`;
    
    setDeclineModal({
      isOpen: true,
      booking,
      message: defaultMessage
    });
  };

  const closeDeclineModal = () => {
    setDeclineModal({ isOpen: false, booking: null, message: '' });
  };

  const handleDeclineWithMessage = async () => {
    if (!beginAction('decline-email')) return;
    try {
      const booking = declineModal.booking;
      const treatments = booking.booking_treatments || [];
      const treatmentNames = treatments.map(bt => bt.treatment_name).join(', ') || 'Appointment';

      await supabaseService.updateBookingStatus(booking.id, 'declined');

      const declineResult = await supabaseService.sendNotification('declined', {
        name: booking.name,
        email: booking.email,
        date: booking.date,
        treatmentNames,
        declineMessage: declineModal.message,
        calendar_event_id: booking.calendar_event_id
      });

      setBookings(prev => prev.map(b =>
        b.id === booking.id ? { ...b, status: 'declined' } : b
      ));

      const emailOk = declineResult?.emailResults?.[0]?.ok;
      const calendarDeleted = declineResult?.calendarDeleted;
      const hadCalendarEvent = !!booking.calendar_event_id;

      if (emailOk && (!hadCalendarEvent || calendarDeleted)) {
        showToast('success', 'Booking declined — client notified by email and calendar event removed.');
      } else if (emailOk && hadCalendarEvent && !calendarDeleted) {
        pinNotice('warning', 'Booking declined — client notified, but calendar event could not be removed. Delete it manually.');
      } else if (!emailOk && (!hadCalendarEvent || calendarDeleted)) {
        pinNotice('warning', 'Booking declined, but the client email may not have been delivered. Contact them directly.');
      } else {
        pinNotice('warning', 'Booking declined, but email was not delivered and calendar event could not be removed. Check manually.');
      }

      closeDeclineModal();
      loadBookings();
    } catch (err) {
      console.error('Error declining booking:', err);
      showToast('error', 'Failed to decline booking. Please try again.');
    } finally {
      endAction();
    }
  };

  const handleDeclineWithoutEmail = async () => {
    if (!beginAction('decline-silent')) return;
    try {
      const booking = declineModal.booking;

      await supabaseService.updateBookingStatus(booking.id, 'declined');

      const declineResult = await supabaseService.sendNotification('declined_no_email', {
        name: booking.name,
        calendar_event_id: booking.calendar_event_id
      });

      setBookings(prev => prev.map(b =>
        b.id === booking.id ? { ...b, status: 'declined' } : b
      ));

      const calendarDeleted = declineResult?.calendarDeleted;
      const hadCalendarEvent = !!booking.calendar_event_id;

      if (!hadCalendarEvent || calendarDeleted) {
        showToast('success', 'Booking declined — no email sent, calendar event removed.');
      } else {
        pinNotice('warning', 'Booking declined — no email sent, but calendar event could not be removed. Delete it manually.');
      }

      closeDeclineModal();
      loadBookings();
    } catch (err) {
      console.error('Error declining booking:', err);
      showToast('error', 'Failed to decline booking. Please try again.');
    } finally {
      endAction();
    }
  };

  // ==================== CANCEL ====================

  const handleCancelApproved = async (booking) => {
    if (!beginAction(`cancel:${booking.id}`)) return;

    try {
      if (!window.confirm(`Cancel appointment for ${booking.name} on ${formatDate(booking.date)} at ${formatTime(booking.time)}? This will send them a cancellation email and remove it from your calendar.`)) {
        return;
      }

      const treatments = booking.booking_treatments || [];
      const treatmentNames = treatments.map(bt => bt.treatment_name).join(', ') || 'Appointment';

      await supabaseService.updateBookingStatus(booking.id, 'cancelled');

      // Send cancellation email and delete from calendar
      const cancelResult = await supabaseService.sendNotification('cancelled', {
        name: booking.name,
        email: booking.email,
        date: booking.date,
        time: booking.time,
        treatmentNames,
        calendar_event_id: booking.calendar_event_id,
        total_duration: booking.total_duration
      });

      setBookings(prev => prev.map(b =>
        b.id === booking.id ? { ...b, status: 'cancelled' } : b
      ));

      // Same four-way as declining, and for a sharper reason: since availability
      // started reading every event in her diary as busy, an event left behind
      // by a failed delete blocks that slot for good. Nothing retries it and
      // nothing sweeps for it, so a success toast here means the slot is gone
      // and no screen anywhere will ever say why.
      const emailOk = cancelResult?.emailResults?.[0]?.ok;
      const calendarDeleted = cancelResult?.calendarDeleted;
      const hadCalendarEvent = !!booking.calendar_event_id;

      if (emailOk && (!hadCalendarEvent || calendarDeleted)) {
        showToast('success', 'Appointment cancelled — client has been notified by email.');
      } else if (emailOk && hadCalendarEvent && !calendarDeleted) {
        pinNotice('warning', 'Appointment cancelled and the client notified, but the calendar event could not be removed. Delete it manually or that time stays blocked.');
      } else if (!emailOk && (!hadCalendarEvent || calendarDeleted)) {
        pinNotice('warning', 'Appointment cancelled, but the client email may not have been delivered. Contact them directly.');
      } else {
        pinNotice('warning', 'Appointment cancelled, but the client email was not delivered and the calendar event could not be removed. Check both.');
      }

      loadBookings();
    } catch (err) {
      console.error('Error cancelling booking:', err);
      showToast('error', 'Failed to cancel booking. Please try again.');
    } finally {
      endAction();
    }
  };

  // What to actually charge. Anything sold at the chair lives in products_total_cents,
  // and she is the one taking the money, so it has to be in the number she reads.
  const renderAmountToCharge = (booking) => {
    const treatments = Number(booking.total_price_cents) || 0;
    const products = Number(booking.products_total_cents) || 0;

    const money = (cents) => {
      const v = cents / 100;
      return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
    };

    if (!treatments && !products) return null;

    return (
      <div className="info-row">
        <span className="info-label">Price:</span>
        <span className="info-value">
          {money(treatments + products)}
          {products > 0 && (
            <span className="charge-detail"> (incl. {money(products)} products)</span>
          )}
        </span>
      </div>
    );
  };

  // Replaces the Google Calendar embeds that used to sit in these modals.
  // Same purpose — see the shape of the day before picking a time — but read
  // from our own bookings rather than an external iframe.
  const renderDaySchedule = (date) => {
    if (!date) {
      return <p className="day-schedule-empty">Pick a date to see what's booked.</p>;
    }

    const onDay = bookings
      .filter(b => b.status === 'approved' && b.date === date && b.time)
      .sort((a, b) => a.time.localeCompare(b.time));

    if (onDay.length === 0) {
      return <p className="day-schedule-empty">Nothing booked — the day is clear.</p>;
    }

    return (
      <ul className="day-schedule">
        {onDay.map(b => {
          const treatments = (b.booking_treatments || [])
            .map(bt => bt.treatment_name)
            .join(', ');
          return (
            <li key={b.id}>
              <span className="day-schedule-time">
                {formatTime(b.time)}
                {b.total_duration ? ` · ${b.total_duration}m` : ''}
              </span>
              <span className="day-schedule-who">
                {b.name}{treatments ? ` — ${treatments}` : ''}
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  // ==================== HELPERS ====================

  const generateTimeOptions = (startTime, endTime) => {
    const options = [];
    const start = startTime ? new Date(`2000-01-01T${startTime}`) : new Date('2000-01-01T08:00');
    const end = endTime ? new Date(`2000-01-01T${endTime}`) : new Date('2000-01-01T18:00');

    let current = start;
    while (current <= end) {
      const timeStr = current.toTimeString().slice(0, 5);
      options.push(timeStr);
      current = new Date(current.getTime() + 15 * 60000);
    }

    return options;
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const approvedBookings = bookings.filter(b => b.status === 'approved');

  // "Sat 2 Aug 2026". en-NZ puts a comma after the weekday; the rest of the site
  // drops it, so this does too.
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString + 'T00:00:00')
        .toLocaleDateString('en-NZ', {
          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        })
        .replace(/,/g, '');
    } catch {
      return dateString;
    }
  };

  // "9:00am". Postgres time columns arrive as "09:00:00", so the minutes are
  // trimmed rather than used whole.
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = String(timeString).split(':');
      const h = parseInt(hours, 10);
      if (isNaN(h)) return String(timeString);
      return `${h % 12 || 12}:${(minutes || '00').slice(0, 2)}${h >= 12 ? 'pm' : 'am'}`;
    } catch {
      return timeString;
    }
  };

  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Not specified';
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  // ── Blocked-out time ──────────────────────────────────────────────────────

  // Only the blocks that were fetched for the span currently on screen. A tab
  // switch leaves the previous range in state until the new one lands, and
  // drawing that would put last month's holiday on top of today.
  // Nothing has come back for the span on screen yet. Worth knowing, because
  // "no blocks loaded" and "no blocks exist" look the same and only one of them
  // means she can take a client — a fully blocked day used to paint "Enjoy your
  // day off!" for a beat before the block card replaced it, which is precisely
  // the sentence this screen exists to stop showing wrongly.
  const blocksLoading = !!blocksRange && scheduleBlocks.key !== rangeKey(blocksRange);

  const visibleBlocks = scheduleBlocks.key === rangeKey(blocksRange)
    ? scheduleBlocks
    : { timeOff: [], exceptions: [], google: [], error: '', googleError: '' };

  // A source came back broken, so an empty list means "I could not check", not
  // "you are free". The warning above says so, but a warning is a line of small
  // text and the empty state below it is an emoji and a heading — she reads the
  // loud one. So the loud one has to stop claiming the day is clear.
  const blocksUnknown = Boolean(visibleBlocks.error || visibleBlocks.googleError);

  // Today's row from business_hours. `weekday` is Postgres's extract(dow), so
  // Sunday is 0 — the same convention getDay() uses, which is why these line up
  // without a shift.
  const todayHours = useMemo(() => {
    if (!bookability.hours) return null;
    const dow = new Date().getDay();
    return bookability.hours.find(h => h.weekday === dow) || null;
  }, [bookability.hours]);

  // Closed by the weekly pattern rather than by anything she did today. This is
  // the case that used to render as "Enjoy your day off!" — indistinguishable
  // from a free day, on the one screen meant to explain why a day looks shut.
  const closedByBusinessHours = Boolean(todayHours && !todayHours.is_open);

  // Today is over as far as the booking page is concerned: everything left of
  // closing is inside the minimum-notice window. Not a fault — but she has no
  // way of knowing it from this screen, and it is the single most common reason
  // a client rings instead of booking.
  const noticeClosedToday = Boolean(
    !closedByBusinessHours &&
    !bookability.loading &&
    !bookability.error &&
    bookability.offered === 0 &&
    bookability.filtered === 0
  );

  // One date's piece of a block, for a list that is already about that one day.
  const blockHeadline = (part) =>
    part.allDay ? 'Blocked all day' : `Blocked ${formatTime(part.from)} – ${formatTime(part.to)}`;

  // The whole block, however many days it runs. Used where the list covers a
  // month: a week away belongs there as one line, not as seven identical cards
  // burying the appointments between them.
  const blockSpanHeadline = (row) => {
    const start = splitStamp(row.start_at);
    const end = splitStamp(row.end_at);
    const whole = isWholeDay(start.time, end.time);
    if (start.date === end.date) {
      return whole
        ? `${formatDate(start.date)} · Blocked all day`
        : `${formatDate(start.date)} · Blocked ${formatTime(start.time)} – ${formatTime(end.time)}`;
    }
    return whole
      ? `${formatDate(start.date)} – ${formatDate(end.date)} · Blocked all day`
      : `${formatDate(start.date)} ${formatTime(start.time)} – ${formatDate(end.date)} ${formatTime(end.time)} · Blocked`;
  };

  // The same, for a block that lives in Google Calendar. Kept separate from
  // blockSpanHeadline because the two carry their dates differently and must not
  // share a code path.
  const googleSpanHeadline = (block) => {
    if (block.startDate === block.endDate) {
      return block.allDay
        ? `${formatDate(block.startDate)} · Blocked all day`
        : `${formatDate(block.startDate)} · Blocked ${formatTime(block.startTime)} – ${formatTime(block.endTime)}`;
    }
    return block.allDay
      ? `${formatDate(block.startDate)} – ${formatDate(block.endDate)} · Blocked all day`
      : `${formatDate(block.startDate)} ${formatTime(block.startTime)} – ${formatDate(block.endDate)} ${formatTime(block.endTime)} · Blocked`;
  };

  // Same family as the time-off card and pointedly not the same card. She has
  // two places to block time out and they are unblocked in different places —
  // this one cannot be undone from the Availability tab, so it says where it
  // came from and where to go to lift it.
  const renderGoogleBlockCard = (block, headline, extra) => (
    <div key={block.id} className="block-card google">
      <div className="block-card-header">
        <span className="block-icon" aria-hidden="true">📅</span>
        <h3 className="block-title">{headline}</h3>
        <span className="block-pill">Google Calendar</span>
      </div>
      <div className="block-card-body">
        {block.summary && <p className="block-reason">{block.summary}</p>}
        {extra && <p className="block-note">{extra}</p>}
        <p className="block-note">
          This is an event in your own Google Calendar, not something you set on the
          Availability tab. Nobody can book this time. To open it back up, delete or
          move the event in Google Calendar.
        </p>
      </div>
    </div>
  );

  const renderTimeOffCard = (row, headline, extra) => (
    <div key={`time-off-${row.id}`} className="block-card">
      <div className="block-card-header">
        <span className="block-icon" aria-hidden="true">🚫</span>
        <h3 className="block-title">{headline}</h3>
        <span className="block-pill">Time off</span>
      </div>
      <div className="block-card-body">
        {row.reason && <p className="block-reason">{row.reason}</p>}
        {extra && <p className="block-note">{extra}</p>}
        {/* Blocking never cancelled anything, so this card must not be read as
            "the day is clear" — an appointment underneath it is still coming. */}
        <p className="block-note">
          Nobody can book this time. Anything already in your diary inside it is still there.
        </p>
      </div>
    </div>
  );

  // Pulled out of the Today list unchanged, so a booking and a block can be
  // interleaved in one time-ordered pass over the day.
  const renderTodayBookingCard = (booking) => {
    const treatments = booking.booking_treatments || [];
    const treatmentNames = treatments.map(bt => bt.treatment_name).join(', ') || 'Not specified';

    return (
      <div key={booking.id} className="booking-card today">
        <div className="card-header">
          <div className="time-badge">{formatTime(booking.time)}</div>
          <h3 className="client-name">{booking.name}</h3>
          {booking.client_type && (
            <span className={`client-type-pill ${booking.client_type}`}>
              {booking.client_type === 'new' ? '⭐ New' : '🔁 Returning'}
            </span>
          )}
        </div>

        <div className="card-body">
          <div className="info-row">
            <span className="info-label">Treatment:</span>
            <span className="info-value treatment">{treatmentNames}</span>
          </div>

          {booking.phone && (
            <div className="info-row">
              <span className="info-label">Phone:</span>
              <a href={`tel:${booking.phone}`} className="info-value">{booking.phone}</a>
            </div>
          )}

          {renderAmountToCharge(booking)}

          {booking.notes && (
            <div className="notes-box">
              <span className="info-label">Notes:</span>
              <p className="notes-text">{booking.notes}</p>
            </div>
          )}

          <div className="booking-actions">
            <button
              type="button"
              className="btn-move"
              onClick={() => openMoveModal(booking)}
              disabled={isBusy}
            >
              🕑 Move this
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderExceptionCard = (exception, { withDate = true } = {}) => {
    const closed = !!exception.is_closed;
    const past = exception.exception_date < toISODate(new Date());
    const what = closed
      ? 'Closed all day'
      : `Open ${formatTime(exception.open_time)} – ${formatTime(exception.close_time)}`;
    return (
      <div key={`exception-${exception.id}`} className={`block-card exception ${closed ? 'closed' : 'hours'}`}>
        <div className="block-card-header">
          <span className="block-icon" aria-hidden="true">{closed ? '🚫' : '🕑'}</span>
          <h3 className="block-title">
            {withDate ? `${formatDate(exception.exception_date)} · ${what}` : what}
          </h3>
          <span className="block-pill">{closed ? 'Closed' : 'Different hours'}</span>
        </div>
        <div className="block-card-body">
          {exception.note && <p className="block-reason">{exception.note}</p>}
          <p className="block-note">
            {closed
              ? `A one-off day change, not your usual week. Nobody ${past ? 'could' : 'can'} book this day.`
              : `A one-off change to this day's hours${exception.locations ? `, at ${exception.locations.name}` : ''}. Your usual hours for this weekday ${past ? 'did not' : 'do not'} apply.`}
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading your bookings...</div>
      </div>
    );
  }

  const timeOptions = approvalModal.booking
    ? generateTimeOptions(approvalModal.booking.time_range_start, approvalModal.booking.time_range_end)
    : [];

  return (
    <div className="admin-dashboard">
      <Toast toast={toast} onClose={closeToast} />

      {/* Mounted once at the top level rather than inside the Today tab, so it
          survives a tab switch behind it and keeps its session receipts. */}
      <QuickBookModal
        open={quickBookOpen}
        onClose={() => setQuickBookOpen(false)}
        onBooked={loadBookings}
        showToast={showToast}
        pinNotice={pinNotice}
      />
      <div className="dashboard-centered">
        <div className="bookings-section">
          <header className="admin-header">
            <div>
              <h1>Your Bookings</h1>
              <p className="header-subtitle">Manage your appointments</p>
            </div>
            <button onClick={onLogout} className="logout-btn">Logout</button>
          </header>

          {/* Nothing else in the app shows a dead email provider: bookings keep
              succeeding, so the only symptom is silence. This is the alarm. */}
          {emailFailures.length > 0 && (() => {
            const latest = emailFailures[0];
            const count = emailFailures.length;
            // The query caps at 20, so a full page means "at least this many".
            const countLabel = count >= 20
              ? '20+ emails'
              : `${count} email${count === 1 ? '' : 's'}`;

            return (
              <section className="email-alert" role="alert">
                <div className="email-alert-main">
                  <span className="email-alert-icon" aria-hidden="true">!</span>
                  <div className="email-alert-text">
                    <h2 className="email-alert-title">
                      {countLabel} could not be delivered
                    </h2>
                    <p className="email-alert-body">
                      Bookings are still going through as normal, so nothing else will look
                      wrong — but confirmations, reminders and cancellations are not reaching
                      anyone, and neither are your own alerts. Check the email provider key in
                      Supabase, and contact anyone affected yourself in the meantime.
                    </p>
                    <div className="email-alert-latest">
                      <span className="email-alert-latest-head">
                        Most recent · {formatFailureTime(latest.created_at)}
                      </span>
                      <span className="email-alert-latest-who">
                        <strong>{describeEmailType(latest.email_type)}</strong>
                        {' to '}
                        {latest.recipient || 'an unknown address'}
                      </span>
                      <span className="email-alert-latest-error">
                        {latest.error || 'No error detail was recorded.'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="email-alert-actions">
                  <button
                    type="button"
                    className="email-alert-dismiss"
                    onClick={handleDismissEmailFailures}
                    disabled={failuresDismissing}
                  >
                    {failuresDismissing ? 'Marking…' : "I've seen this"}
                  </button>
                  <span className="email-alert-note">
                    Only hides this warning — it doesn't resend anything.
                  </span>
                </div>
              </section>
            );
          })()}

          {error && <div className="error-banner">{error}</div>}

          {/* Anything a toast said that she still has to do something about.
              The toast itself is gone in five seconds; these are not. */}
          {notices.length > 0 && (
            <section className="pinned-notices" aria-label="Needs your attention">
              {notices.map(n => (
                <div key={n.id} className={`pinned-notice ${n.type}`} role="alert">
                  <div className="pinned-notice-text">
                    <span className="pinned-notice-when">{formatFailureTime(n.at)}</span>
                    <p className="pinned-notice-message">{n.message}</p>
                  </div>
                  <button
                    type="button"
                    className="pinned-notice-dismiss"
                    onClick={() => dismissNotice(n.id)}
                  >
                    Sorted
                  </button>
                </div>
              ))}
            </section>
          )}

          {/* Section bar — top-level navigation */}
          <div className="section-bar">
            <button
              className={`section-btn ${section === 'actions' ? 'active' : ''}`}
              onClick={() => switchSection('actions')}
            >
              <span className="section-icon">⚡</span>
              <span>Actions</span>
              {(pendingBookings.length +
                orders.filter(o => o.status === 'pending').length +
                vouchers.filter(v => v.status === 'pending').length) > 0 && (
                <span className="section-count">
                  {pendingBookings.length +
                   orders.filter(o => o.status === 'pending').length +
                   vouchers.filter(v => v.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              className={`section-btn ${section === 'schedule' ? 'active' : ''}`}
              onClick={() => switchSection('schedule')}
            >
              <span className="section-icon">📅</span>
              <span>Schedule</span>
              {todaysBookings.length > 0 && (
                <span className="section-count section-count-blue">{todaysBookings.length}</span>
              )}
            </button>
            <button
              className={`section-btn ${section === 'clients' ? 'active' : ''}`}
              onClick={() => switchSection('clients')}
            >
              <span className="section-icon">👤</span>
              <span>Clients</span>
            </button>
            <button
              className={`section-btn ${section === 'availability' ? 'active' : ''}`}
              onClick={() => switchSection('availability')}
            >
              <span className="section-icon">🗓️</span>
              <span>Availability</span>
            </button>
            <button
              className={`section-btn ${section === 'shop' ? 'active' : ''}`}
              onClick={() => switchSection('shop')}
            >
              <span className="section-icon">🛍️</span>
              <span>Shop</span>
            </button>
            <button
              className={`section-btn ${section === 'marketing' ? 'active' : ''}`}
              onClick={() => switchSection('marketing')}
            >
              <span className="section-icon">📣</span>
              <span>Marketing</span>
            </button>
          </div>

          {/* Sub-tab bar — changes based on active section */}
          <div className="tab-bar">
            {section === 'actions' && (
              <>
                <button
                  className={`tab ${tab === 'pending' ? 'active' : ''}`}
                  onClick={() => setTab('pending')}
                >
                  <span className="tab-icon">⏳</span>
                  Requests
                  {pendingBookings.length > 0 && <span className="tab-count">{pendingBookings.length}</span>}
                </button>
                <button
                  className={`tab ${tab === 'orders' ? 'active' : ''}`}
                  onClick={() => setTab('orders')}
                >
                  <span className="tab-icon">🛍️</span>
                  Orders
                  {orders.filter(o => o.status === 'pending').length > 0 && (
                    <span className="tab-count">{orders.filter(o => o.status === 'pending').length}</span>
                  )}
                </button>
                <button
                  className={`tab ${tab === 'vouchers' ? 'active' : ''}`}
                  onClick={() => setTab('vouchers')}
                >
                  <span className="tab-icon">🎟️</span>
                  Vouchers
                  {vouchers.filter(v => v.status === 'pending').length > 0 && (
                    <span className="tab-count">{vouchers.filter(v => v.status === 'pending').length}</span>
                  )}
                </button>
              </>
            )}

            {section === 'schedule' && (
              <>
                <button
                  className={`tab ${tab === 'today' ? 'active' : ''}`}
                  onClick={() => setTab('today')}
                >
                  <span className="tab-icon">📅</span>
                  Today
                  {todaysBookings.length > 0 && <span className="tab-count-blue">{todaysBookings.length}</span>}
                </button>
                <button
                  className={`tab ${tab === 'approved' ? 'active' : ''}`}
                  onClick={() => setTab('approved')}
                >
                  <span className="tab-icon">✓</span>
                  Approved
                </button>
              </>
            )}

            {section === 'shop' && (
              <>
                <button
                  className={`tab ${tab === 'addto' ? 'active' : ''}`}
                  onClick={() => setTab('addto')}
                >
                  <span className="tab-icon">🎁</span>
                  Products
                </button>
                <button
                  className={`tab ${tab === 'stock' ? 'active' : ''}`}
                  onClick={() => setTab('stock')}
                >
                  <span className="tab-icon">📦</span>
                  Stock
                  {stockProducts.filter(p => p.stock !== null && p.stock <= 0).length > 0 && (
                    <span className="tab-count tab-count-red">
                      {stockProducts.filter(p => p.stock !== null && p.stock <= 0).length}
                    </span>
                  )}
                </button>
              </>
            )}

            {section === 'marketing' && (
              <>
                <button
                  className={`tab ${tab === 'promos' ? 'active' : ''}`}
                  onClick={() => setTab('promos')}
                >
                  <span className="tab-icon">🎉</span>
                  Promos
                </button>
                <button
                  className={`tab ${tab === 'email' ? 'active' : ''}`}
                  onClick={() => setTab('email')}
                >
                  <span className="tab-icon">✉️</span>
                  Email
                </button>
                <button
                  className={`tab ${tab === 'stats' ? 'active' : ''}`}
                  onClick={() => setTab('stats')}
                >
                  <span className="tab-icon">📊</span>
                  Stats
                </button>
              </>
            )}
          </div>

          {/* Helper text */}
          {tab === 'pending' && pendingBookings.length > 0 && (
            <div className="helper-text">
              👇 Review the client's available time window, then "Set Time &amp; Approve". Once it's confirmed you can still move it from Schedule
            </div>
          )}

          {tab === 'today' && (
            <div className="today-header">
              <h2>{new Date().toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
              {/* One tap from the screen she already has open. This is the path
                  that replaces typing into Google Calendar, so it cannot be
                  buried behind a client search or an existing visit. */}
              <button type="button" className="today-quickbook" onClick={() => setQuickBookOpen(true)}>
                + Book someone in
              </button>
            </div>
          )}

          <div className="bookings-list">
            {/* ==================== TODAY ==================== */}
            {tab === 'today' && (() => {
              const dayISO = toISODate(new Date());
              const exception = visibleBlocks.exceptions.find(e => e.exception_date === dayISO) || null;

              // Everything that occupies a piece of today, in the order the day
              // runs. A block sits in the same stream as the appointments so the
              // gap in the diary has something in it — but it is drawn as its own
              // kind of card, because nobody is coming to it.
              const entries = [
                ...todaysBookings.map(booking => ({
                  kind: 'booking', at: minutesOfTime(booking.time), booking
                })),
                ...visibleBlocks.timeOff
                  .map(row => ({ row, part: timeOffOnDate(row, dayISO) }))
                  .filter(item => item.part)
                  .map(({ row, part }) => ({
                    kind: 'timeoff', at: minutesOfTime(part.from), row, part
                  })),
                // Her Google Calendar, minus the appointments the system put
                // there itself — those are already in this list as bookings.
                ...visibleBlocks.google
                  .filter(block => !isBookingEcho(block, todaysBookings))
                  .map(block => ({ block, part: googleBlockOnDate(block, dayISO) }))
                  .filter(item => item.part)
                  .map(({ block, part }) => ({
                    kind: 'google', at: minutesOfTime(part.from), block, part
                  })),
              ].sort((a, b) => a.at - b.at);

              return (
                <>
                  {visibleBlocks.error && (
                    <div className="helper-text">⚠️ {visibleBlocks.error}</div>
                  )}

                  {visibleBlocks.googleError && (
                    <div className="helper-text">⚠️ {visibleBlocks.googleError}</div>
                  )}

                  {/* What the booking page is actually offering today. The cards
                      below say what is in the way; this says what came out the
                      other end, which is the question she is really asking when
                      she says the site is showing her booked while she is free. */}
                  <div className="bookability">
                    <div className="bookability-head">
                      <span className="bookability-title">What the booking page is offering today</span>
                      {bookability.loading && <span className="bookability-note">checking…</span>}
                    </div>

                    {bookability.error ? (
                      <p className="bookability-warn">⚠️ {bookability.error}</p>
                    ) : (
                      <ul className="bookability-list">
                        <li>
                          <strong>Your hours</strong>
                          <span>
                            {!todayHours
                              ? 'Not set for today'
                              : todayHours.is_open
                                ? `Open ${formatTimeRange(todayHours.open_time, todayHours.close_time)}`
                                : 'Closed today — set in Availability, not a one-off'}
                          </span>
                        </li>

                        <li>
                          <strong>Bookable online now</strong>
                          <span>
                            {closedByBusinessHours
                              ? 'Nothing — you are closed today'
                              : bookability.offered === 0
                                ? 'Nothing left today'
                                : `${bookability.offered} start ${bookability.offered === 1 ? 'time' : 'times'} (for a 60 min treatment)`}
                          </span>
                        </li>

                        {/* The number that was computed on every single request
                            and never shown to anyone. If this is high on a day
                            she believes is free, her Google Calendar is the
                            place to look. */}
                        {bookability.filtered > 0 && (
                          <li>
                            <strong>Hidden by your Google Calendar</strong>
                            <span>
                              {bookability.filtered} {bookability.filtered === 1 ? 'time' : 'times'} — if that looks wrong, check today in Google Calendar
                            </span>
                          </li>
                        )}

                        {noticeClosedToday && bookability.settings?.min_notice_hours > 0 && (
                          <li>
                            <strong>Why nothing is left</strong>
                            <span>
                              You take bookings {bookability.settings.min_notice_hours} hours ahead, so today is
                              closed to online booking. You can still add someone yourself.
                            </span>
                          </li>
                        )}

                        {bookability.lastBookableDate && (
                          <li>
                            <strong>Booking as far ahead as</strong>
                            <span>{formatDate(bookability.lastBookableDate)}</span>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>

                  {exception && renderExceptionCard(exception, { withDate: false })}

                  {/* A Google block covering today lands in `entries`, so it
                      suppresses the empty state the same way a booking does —
                      the day never reads as free while something is on it. */}
                  {entries.length === 0 ? (
                    blocksLoading ? (
                      <div className="block-loading">Checking your blocked-out time…</div>
                    ) : exception && exception.is_closed ? (
                      // A closed day already says what it is a few pixels up. Adding
                      // "enjoy your day off" under it just makes it look empty again.
                      null
                    ) : blocksUnknown ? (
                      // Nothing in the diary and no way to know whether that is the
                      // truth. Saying so is the whole point of the feature.
                      <div className="no-bookings">
                        <div className="empty-icon">⚠️</div>
                        <h3>Today looks clear — but I couldn't check it all</h3>
                        <p>Your blocked-out time didn't load, so this isn't the whole day. Check the Availability tab and your Google Calendar before you offer anyone this time.</p>
                      </div>
                    ) : closedByBusinessHours ? (
                      // Shut by the weekly pattern, not by choice today. This
                      // used to draw "Enjoy your day off!", which looks exactly
                      // like a free day with nothing booked — so a day nobody
                      // could ever book read as a quiet one, and the setting
                      // behind it stayed invisible.
                      <div className="no-bookings">
                        <div className="empty-icon">🔒</div>
                        <h3>You're closed today</h3>
                        <p>
                          Your opening hours have {new Date().toLocaleDateString('en-NZ', { weekday: 'long' })}s
                          set to closed, so nobody can book today online. Change it in the Availability tab.
                        </p>
                      </div>
                    ) : (
                      <div className="no-bookings">
                        <div className="empty-icon">😌</div>
                        <h3>No appointments today</h3>
                        <p>
                          {noticeClosedToday && bookability.settings?.min_notice_hours > 0
                            ? `Nothing booked, and today is past your ${bookability.settings.min_notice_hours}-hour notice window, so it can't be booked online any more. You can still add someone yourself.`
                            : 'Enjoy your day off!'}
                        </p>
                      </div>
                    )
                  ) : (
                    entries.map(entry => {
                      if (entry.kind === 'timeoff') {
                        return renderTimeOffCard(
                          entry.row,
                          blockHeadline(entry.part),
                          entry.part.spansDays
                            ? `Part of a longer block, ${formatDate(entry.part.start.date)} – ${formatDate(entry.part.end.date)}.`
                            : ''
                        );
                      }
                      if (entry.kind === 'google') {
                        return renderGoogleBlockCard(
                          entry.block,
                          blockHeadline(entry.part),
                          entry.part.spansDays
                            ? `Part of a longer event, ${formatDate(entry.block.startDate)} – ${formatDate(entry.block.endDate)}.`
                            : ''
                        );
                      }
                      return renderTodayBookingCard(entry.booking);
                    })
                  )}
                </>
              );
            })()}

            {/* ==================== PENDING ==================== */}
            {tab === 'pending' && (
              <>
                {pendingBookings.length === 0 ? (
                  <div className="no-bookings">
                    <div className="empty-icon">✨</div>
                    <h3>All caught up!</h3>
                    <p>No pending booking requests</p>
                  </div>
                ) : (
                  pendingBookings.map(booking => {
                    const treatments = booking.booking_treatments || [];
                    const treatmentNames = treatments.map(bt => bt.treatment_name).join(', ') || 'Not specified';

                    return (
                      <div key={booking.id} className="booking-card pending">
                        <div className="card-header">
                          <h3 className="client-name">{booking.name}</h3>
                          <span className="status-pill pending">⏳ Pending</span>
                        </div>

                        <div className="card-body">
                          <div className="info-row">
                            <span className="info-label">Treatment:</span>
                            <span className="info-value treatment">{treatmentNames}</span>
                          </div>

                          <div className="info-row">
                            <span className="info-label">Date:</span>
                            <span className="info-value datetime">{formatDate(booking.date)}</span>
                          </div>

                          <div className="info-row">
                            <span className="info-label">Available:</span>
                            <span className="info-value available-time">{formatTimeRange(booking.time_range_start, booking.time_range_end)}</span>
                          </div>

                          {booking.phone && (
                            <div className="info-row">
                              <span className="info-label">Phone:</span>
                              <a href={`tel:${booking.phone}`} className="info-value">{booking.phone}</a>
                            </div>
                          )}

                          {booking.email && (
                            <div className="info-row">
                              <span className="info-label">Email:</span>
                              <a href={`mailto:${booking.email}`} className="info-value">{booking.email}</a>
                            </div>
                          )}

                          {booking.notes && (
                            <div className="notes-box">
                              <span className="info-label">Notes:</span>
                              <p className="notes-text">{booking.notes}</p>
                            </div>
                          )}
                        </div>

                        <div className="card-actions">
                          <button
                            className="btn-approve"
                            onClick={() => openApprovalModal(booking)}
                          >
                            ✓ Set Time & Approve
                          </button>
                          <button
                            className="btn-decline"
                            onClick={() => openDeclineModal(booking)}
                          >
                            ✕ Decline
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}

            {/* ==================== APPROVED ==================== */}
            {tab === 'approved' && (() => {
              const [selYear, selMonth] = approvedMonth.split('-').map(Number);
              const monthApproved = approvedBookings.filter(b => {
                if (!b.date) return false;
                const d = new Date(b.date + 'T00:00:00');
                return d.getFullYear() === selYear && d.getMonth() + 1 === selMonth;
              }).sort((a, b) => a.date < b.date ? -1 : 1);

              const newClients = monthApproved.filter(b => b.client_type === 'new').length;
              // eslint-disable-next-line no-unused-vars
              const returningClients = monthApproved.filter(b => b.client_type === 'returning').length;
              // Local date, not toISOString() — a UTC date reads as yesterday
              // here until early afternoon, which back-dated "upcoming".
              const today = toISODate(new Date());
              const upcoming = monthApproved.filter(b => b.date >= today).length;

              const parsePrice = (p) => { const n = parseFloat((p || '').toString().replace(/[^0-9.]/g, '')); return isNaN(n) ? 0 : n; };
              const monthRevenue = monthApproved.reduce((sum, b) => sum + parsePrice(b.total_price), 0);

              // Blocked-out time runs through the same date-ordered list as the
              // bookings, so a quiet month can say why it was quiet. A block that
              // spans days stays one card here — a fortnight away split into
              // fourteen cards would bury the appointments either side of it.
              const monthEntries = [
                ...monthApproved.map(booking => ({
                  kind: 'booking', date: booking.date, at: minutesOfTime(booking.time), booking
                })),
                // A day change is about the whole day, so it leads it.
                ...visibleBlocks.exceptions.map(exception => ({
                  kind: 'exception', date: exception.exception_date, at: -2, exception
                })),
                ...visibleBlocks.timeOff.map(row => {
                  const start = splitStamp(row.start_at);
                  const whole = isWholeDay(start.time, splitStamp(row.end_at).time);
                  return {
                    kind: 'timeoff',
                    // Its real start date, which may be in the month before this
                    // one — a holiday running into this month belongs at the top.
                    date: start.date,
                    at: whole ? -1 : minutesOfTime(start.time),
                    row,
                  };
                }),
                // Her Google Calendar, minus the appointments the system wrote
                // there itself — those are already in this list as bookings.
                ...visibleBlocks.google
                  .filter(block => !isBookingEcho(block, monthApproved))
                  .map(block => ({
                    kind: 'google',
                    // Its real start date, which for an event running into this
                    // month from the last one is before the month begins.
                    date: block.startDate,
                    at: block.allDay ? -1 : minutesOfTime(block.startTime),
                    block,
                  })),
              ].sort((a, b) => (a.date === b.date ? a.at - b.at : (a.date < b.date ? -1 : 1)));

              return (
                <>
                  <div className="addto-section-header">
                    <h2 className="addto-section-title">✓ Approved Clients</h2>
                    <input
                      type="month"
                      aria-label="Month to show"
                      value={approvedMonth}
                      onChange={e => setApprovedMonth(e.target.value)}
                      className="admin-input admin-input--inline"
                    />
                  </div>

                  <div className="stat-grid">
                    <div className="stat-card">
                      <p className="stat-label">This Month</p>
                      <p className="stat-value">{monthApproved.length}</p>
                      <p className="stat-label">approved</p>
                    </div>
                    <div className="stat-card">
                      <p className="stat-label">Upcoming</p>
                      <p className="stat-value">{upcoming}</p>
                      <p className="stat-label">still ahead</p>
                    </div>
                    <div className="stat-card">
                      <p className="stat-label">New Clients</p>
                      <p className="stat-value">{newClients}</p>
                      <p className="stat-label">⭐ first-timers</p>
                    </div>
                    <div className="stat-card">
                      <p className="stat-label">Revenue</p>
                      <p className="stat-value">${monthRevenue.toFixed(0)}</p>
                      <p className="stat-label">this month</p>
                    </div>
                  </div>

                  {visibleBlocks.error && (
                    <div className="helper-text">⚠️ {visibleBlocks.error}</div>
                  )}

                  {visibleBlocks.googleError && (
                    <div className="helper-text">⚠️ {visibleBlocks.googleError}</div>
                  )}

                  {/* A month can hold blocked-out time and no appointments at
                      all. The list is then not empty, so the empty state below
                      never fires — but "no approved bookings" is still the true
                      and useful thing to say about that month, and without this
                      it goes unsaid. Both wordings are reachable: this one when
                      there is blocked time to show, the one below when there is
                      nothing in the month whatsoever. */}
                  {monthApproved.length === 0 && monthEntries.length > 0 && (
                    <div className="no-bookings">
                      <div className="empty-icon">📅</div>
                      <h3>No approved bookings for this month</h3>
                      <p>Only your blocked-out time, below</p>
                    </div>
                  )}

                  {monthEntries.length === 0 ? (
                    blocksLoading ? (
                      <div className="block-loading">Checking your blocked-out time…</div>
                    ) : blocksUnknown ? (
                      // The bookings are trustworthy — they loaded. The blocks
                      // beside them are not, and an empty month is exactly where
                      // that difference decides whether she offers the time away.
                      <div className="no-bookings">
                        <div className="empty-icon">⚠️</div>
                        <h3>No approved bookings — but I couldn't check it all</h3>
                        <p>Your blocked-out time for this month didn't load, so this isn't the whole picture. Check the Availability tab and your Google Calendar before you offer anyone this time.</p>
                      </div>
                    ) : (
                      <div className="no-bookings">
                        <div className="empty-icon">📅</div>
                        <h3>No approved bookings for this month</h3>
                        <p>Try selecting a different month above</p>
                      </div>
                    )
                  ) : (
                    monthEntries.map(entry => {
                      if (entry.kind === 'exception') return renderExceptionCard(entry.exception);
                      if (entry.kind === 'timeoff') {
                        return renderTimeOffCard(entry.row, blockSpanHeadline(entry.row), '');
                      }
                      if (entry.kind === 'google') {
                        return renderGoogleBlockCard(entry.block, googleSpanHeadline(entry.block), '');
                      }

                      const booking = entry.booking;
                      const treatments = booking.booking_treatments || [];
                      const treatmentNames = treatments.map(bt => bt.treatment_name).join(', ') || 'Not specified';
                      const isPast = booking.date < today;

                      return (
                        <div key={booking.id} className="booking-card approved" style={isPast ? { opacity: 0.65 } : {}}>
                          <div className="card-header">
                            <h3 className="client-name">{booking.name}</h3>
                            <span className="status-pill approved">{isPast ? '✓ Completed' : '✓ Confirmed'}</span>
                            {booking.client_type && (
                              <span className={`client-type-pill ${booking.client_type}`}>
                                {booking.client_type === 'new' ? '⭐ New' : '🔁 Returning'}
                              </span>
                            )}
                          </div>

                          <div className="card-body">
                            <div className="info-row">
                              <span className="info-label">Treatment:</span>
                              <span className="info-value treatment">{treatmentNames}</span>
                            </div>

                            <div className="info-row">
                              <span className="info-label">Date & Time:</span>
                              <span className="info-value datetime">{formatDate(booking.date)} at {formatTime(booking.time)}</span>
                            </div>

                            {booking.phone && (
                              <div className="info-row">
                                <span className="info-label">Phone:</span>
                                <a href={`tel:${booking.phone}`} className="info-value">{booking.phone}</a>
                              </div>
                            )}

                            {booking.email && (
                              <div className="info-row">
                                <span className="info-label">Email:</span>
                                <a href={`mailto:${booking.email}`} className="info-value">{booking.email}</a>
                              </div>
                            )}

                            {booking.notes && (
                              <div className="notes-box">
                                <span className="info-label">Notes:</span>
                                <p className="notes-text">{booking.notes}</p>
                              </div>
                            )}

                            {!isPast && (
                              <div className="booking-actions">
                                <button
                                  type="button"
                                  className="btn-move"
                                  onClick={() => openMoveModal(booking)}
                                  disabled={isBusy}
                                >
                                  🕑 Move this
                                </button>
                                <button
                                  className="btn-cancel"
                                  onClick={() => handleCancelApproved(booking)}
                                  disabled={isBusy}
                                >
                                  {actionBusy === `cancel:${booking.id}`
                                    ? 'Cancelling…'
                                    : '🗑️ Cancel'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              );
            })()}

            {tab === 'clients' && <ClientsPanel showToast={showToast} />}

            {tab === 'availability' && <AvailabilityPanel showToast={showToast} />}

            {/* ==================== ORDERS ==================== */}
            {tab === 'orders' && (
              <>
                {shopLoading ? (
                  <div className="no-bookings"><p>Loading orders...</p></div>
                ) : orders.length === 0 ? (
                  <div className="no-bookings">
                    <div className="empty-icon">🛍️</div>
                    <h3>No orders yet</h3>
                    <p>Shop orders will appear here</p>
                  </div>
                ) : (
                  orders.map(order => {
                    const items = order.shop_order_items || [];
                    return (
                      <div key={order.id} className={`booking-card ${order.status === 'fulfilled' ? 'approved' : order.status === 'responded' ? 'rescheduled' : 'pending'}`}>
                        <div className="card-header">
                          <h3 className="client-name">{order.customer_name}</h3>
                          {/* Delete lives up here, a card's height away from Respond /
                              Mark Fulfilled. Down in the action row it was a 26px target
                              flush against two buttons she taps every day. */}
                          <div className="card-header-controls">
                            <span className={`status-pill ${order.status === 'fulfilled' ? 'approved' : order.status === 'responded' ? 'rescheduled' : 'pending'}`}>
                              {order.status === 'fulfilled' ? '✓ Fulfilled' : order.status === 'responded' ? '📧 Responded' : '⏳ Pending'}
                            </span>
                            <button
                              type="button"
                              className="btn-card-delete"
                              onClick={() => handleDeleteOrder(order)}
                              disabled={isBusy}
                              aria-label={`Delete order from ${order.customer_name}`}
                            >
                              <span aria-hidden="true">🗑</span>
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="info-row">
                            <span className="info-label">Email:</span>
                            <a href={`mailto:${order.email}`} className="info-value">{order.email}</a>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Fulfillment:</span>
                            <span className="info-value">{order.fulfillment === 'pickup' ? '🏠 Pickup' : `📦 Delivery — ${order.address || ''}`}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Items:</span>
                            <span className="info-value treatment">
                              {items.map(i => `${i.product_name} ×${i.quantity}`).join(', ') || '—'}
                            </span>
                          </div>
                          {order.notes && (
                            <div className="notes-box">
                              <span className="info-label">Notes:</span>
                              <p className="notes-text">{order.notes}</p>
                            </div>
                          )}
                          {order.response_message && (
                            <div className="reschedule-message-box">
                              <span className="info-label">Your response:</span>
                              <p className="reschedule-message">"{order.response_message}"</p>
                            </div>
                          )}
                          <div className="info-row" style={{ marginTop: '6px' }}>
                            <span className="info-label">Received:</span>
                            <span className="info-value">{new Date(order.created_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        </div>
                        {order.status !== 'fulfilled' && (
                          <div className="card-actions">
                            <button className="btn-approve" onClick={() => openOrderModal(order)}>
                              📧 Respond to Order
                            </button>
                            <button
                              className="btn-reschedule"
                              onClick={() => handleMarkFulfilled(order)}
                              disabled={isBusy}
                            >
                              {actionBusy === `fulfil:${order.id}` ? 'Saving…' : '✓ Mark Fulfilled'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </>
            )}

            {/* ==================== VOUCHERS ==================== */}
            {tab === 'vouchers' && (
              <>
                {/* Redeeming belongs to the tab, not to a row. Someone hands her
                    a voucher at the door and she works from the code on it —
                    which row it happens to be is something she'd have to go and
                    find. Sticky, so it is under her thumb wherever the list has
                    scrolled to. */}
                <div className="voucher-redeem-bar">
                  <div className="voucher-redeem-bar-text">
                    <strong>Someone's paying with a voucher?</strong>
                    <span>Take it off here. You'll type the code off their card, and you can take part of it if the treatment costs less.</span>
                  </div>
                  <button
                    type="button"
                    className="btn-approve btn-tiny"
                    onClick={openRedeemModal}
                    disabled={isBusy}
                  >
                    🎟️ Mark a voucher as used
                  </button>
                </div>

                {vouchersLoading ? (
                  <div className="no-bookings"><p>Loading vouchers...</p></div>
                ) : vouchers.length === 0 ? (
                  <div className="no-bookings">
                    <div className="empty-icon">🎟️</div>
                    <h3>No voucher requests yet</h3>
                    <p>Gift voucher requests will appear here</p>
                  </div>
                ) : (
                  vouchers.map(voucher => {
                    // 'ready' and 'collected' are the old hand-run lifecycle and
                    // still sit on live rows. They are read-only history now, but
                    // they have to keep displaying — rewriting them would be
                    // claiming a code was issued when none ever was.
                    const done = voucher.status === 'redeemed' || voucher.status === 'collected';
                    // Part-used is a live voucher, not a closed one. It has
                    // money on it, the client keeps the same card, and the
                    // row has to say so at a glance or she'll turn one away.
                    const partUsed = voucher.status === 'part_used';
                    const live = voucher.status === 'issued' || voucher.status === 'ready' || partUsed;
                    const left = formatCents(voucher.balance_cents);
                    const pill =
                      voucher.status === 'redeemed' ? { bg: '#27ae60', label: '✓ All used' }
                      : voucher.status === 'collected' ? { bg: '#27ae60', label: '✓ Collected' }
                      : partUsed ? { bg: '#8e6bb0', label: left ? `◑ $${left} left` : '◑ Part used' }
                      : voucher.status === 'issued' ? { bg: '#2980b9', label: '✉ Issued' }
                      : voucher.status === 'ready' ? { bg: '#2980b9', label: '✉ Ready' }
                      : voucher.status === 'declined' ? { bg: '#7f8c8d', label: '✕ Declined' }
                      : { bg: '#e67e22', label: '⏳ Awaiting payment' };

                    return (
                    <div key={voucher.id} className={`booking-card ${done ? 'approved' : live ? 'rescheduled' : 'pending'}`}>
                      <div className="card-header">
                        <h3 className="client-name">{voucher.purchaser_name}</h3>
                        <span
                          className="status-pill"
                          style={{ background: pill.bg, color: 'white' }}
                        >
                          {pill.label}
                        </span>
                      </div>
                      <div className="card-body">
                        {voucher.code && (
                          <div className="voucher-issued-box">
                            <div className="voucher-issued-row">
                              <span className="info-label">Code:</span>
                              <span className="voucher-code">{voucher.code}</span>
                            </div>
                            <div className="voucher-issued-row">
                              <span className="info-label">Valid until:</span>
                              <span className="info-value">{formatISODate(voucher.expires_at)}</span>
                            </div>
                            {/* Spelled out under the code as well as in the
                                pill, because this is the number she reads back
                                to whoever is standing there: what is left, and
                                what it started as. */}
                            {partUsed && left && (
                              <div className="voucher-issued-row">
                                <span className="info-label">Still to spend:</span>
                                <span className="voucher-balance">
                                  ${left} of ${formatAmount(voucher.amount)}
                                </span>
                              </div>
                            )}
                            {/* Only ever "Used on". redeemed_at is stamped when
                                the last of a voucher goes, so a part-used row
                                never reaches here — it said "Last used" over a
                                date that was never written. The dates of the
                                part-spends live in voucher_redemptions, which
                                this row does not carry, so it says nothing
                                about them rather than guessing. */}
                            {voucher.redeemed_at && (
                              <div className="voucher-issued-row">
                                <span className="info-label">Used on:</span>
                                <span className="info-value">
                                  {new Date(voucher.redeemed_at).toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              </div>
                            )}
                            {voucher.redeemed_note && (
                              <p className="notes-text">{voucher.redeemed_note}</p>
                            )}
                          </div>
                        )}
                        <div className="info-row">
                          <span className="info-label">Amount:</span>
                          <span className="info-value">${formatAmount(voucher.amount)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Email:</span>
                          <span className="info-value">{voucher.purchaser_email}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Phone:</span>
                          <span className="info-value">{voucher.purchaser_phone}</span>
                        </div>
                        {/* Delivery method and pickup date used to be drawn here.
                            Every voucher is emailed — there is no pickup flow and
                            no button that ever honoured a collection date, so
                            showing one only asked her to act on something that
                            isn't hers to do. Legacy rows still hold both in the
                            database; they just don't get repeated back at her. */}
                        {voucher.recipient_name && (
                          <div className="info-row">
                            <span className="info-label">For:</span>
                            <span className="info-value">{voucher.recipient_name}</span>
                          </div>
                        )}
                        {voucher.notes && (
                          <div className="notes-box">
                            <span className="info-label">Notes:</span>
                            <p className="notes-text">{voucher.notes}</p>
                          </div>
                        )}
                        <div className="info-row" style={{ marginTop: '6px' }}>
                          <span className="info-label">Received:</span>
                          <span className="info-value">{new Date(voucher.created_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      </div>
                      <div className="card-actions">
                        {voucher.status === 'pending' && (
                          <button
                            className="btn-approve"
                            onClick={() => handleIssueVoucher(voucher)}
                            disabled={isBusy}
                          >
                            {actionBusy === `voucher:${voucher.id}` ? 'Issuing…' : '💰 Payment received'}
                          </button>
                        )}
                        {/* Marking one used lives at the top of the tab now, not
                            here: it acts on the code she types, and a button on
                            a row promises it acts on that row. What belongs on a
                            row is sending that row's own email again. */}
                        {/* Part-used rows keep this too: the card is still
                            live, so a lost email is still worth re-sending. */}
                        {(voucher.status === 'issued' || partUsed) && (
                          <button
                            className="btn-reschedule"
                            onClick={() => handleResendVoucherEmail(voucher)}
                            disabled={isBusy}
                          >
                            {actionBusy === `voucher:${voucher.id}` ? 'Sending…' : '✉ Re-send voucher email'}
                          </button>
                        )}
                        {/* Legacy rows only: these never got a code, so there is
                            nothing for her to type and nothing to redeem. */}
                        {voucher.status === 'ready' && (
                          <button
                            className="btn-reschedule"
                            onClick={() => handleVoucherStatus(voucher, 'collected')}
                            disabled={isBusy}
                          >
                            {actionBusy === `voucher:${voucher.id}` ? 'Saving…' : '✓ Mark Collected'}
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })
                )}
              </>
            )}

            {/* ==================== ADD TO ==================== */}
            {tab === 'addto' && (
              <>
                {/* ── Products section ── */}
                <div className="addto-section-header">
                  <h2 className="addto-section-title">📦 Shop Products</h2>
                  <div className="header-actions">
                    <button
                      className="btn-approve btn-tiny"
                      onClick={async () => {
                        const toActivate = shopProducts.filter(p => !p.is_active && p.stock !== null && p.stock > 0);
                        if (toActivate.length === 0) { alert('No hidden products with stock to activate.'); return; }
                        if (!window.confirm(`Set ${toActivate.length} product(s) with stock to visible?`)) return;
                        try {
                          await Promise.all(toActivate.map(p => supabaseService.updateShopProduct(p.id, { is_active: true })));
                          loadShopData();
                        } catch (err) {
                          alert('Failed to activate products.');
                        }
                      }}
                    >Activate Stocked</button>
                    <button className="btn-add-small" onClick={openAddProduct}>+ Add Product</button>
                  </div>
                </div>
                <input
                  type="search"
                  aria-label="Search products"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="admin-input admin-input--search"
                />
                {shopLoading ? (
                  <div className="no-bookings"><p>Loading...</p></div>
                ) : shopProducts.length === 0 ? (
                  <p className="empty-note">No products yet — click "+ Add Product" to get started</p>
                ) : (
                  <div className="cat-list cat-list--spaced">
                    {Object.entries(shopProducts.filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase())).reduce((acc, p) => {
                      if (!acc[p.category]) acc[p.category] = [];
                      acc[p.category].push(p);
                      return acc;
                    }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                      <div key={category} className="cat-group">
                        <button
                          type="button"
                          className={`cat-toggle ${expandedCats['prod_' + category] ? 'open' : ''}`}
                          aria-expanded={!!(expandedCats['prod_' + category] || productSearch)}
                          onClick={() => toggleCat('prod_' + category)}
                        >
                          <span>{category} ({items.length})</span>
                          <span className="cat-chevron" aria-hidden="true">{(expandedCats['prod_' + category] || productSearch) ? '▼' : '▶'}</span>
                        </button>
                        {(expandedCats['prod_' + category] || productSearch) && (
                          <div className="products-table-wrap">
                            {/* Below 700px these rows become stacked cards (see admin.css);
                                data-label is what supplies the column heading there. */}
                            <table className="products-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Price</th>
                                  <th>Active</th>
                                  <th><span className="visually-hidden">Actions</span></th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...items].sort((a, b) => {
                                  const rank = (p) => p.stock !== null && p.stock > 0 ? 0 : p.stock === null ? 1 : 2;
                                  return rank(a) - rank(b);
                                }).map(product => (
                                  <tr key={product.id}>
                                    <td data-label="Name" className="cell-name">{product.name}</td>
                                    <td data-label="Price" className="cell-price">{product.price}</td>
                                    <td data-label="Active">
                                      <button
                                        type="button"
                                        className={`pill-toggle ${product.is_active ? 'status-pill approved' : 'status-pill pending'}`}
                                        onClick={async () => {
                                          await supabaseService.updateShopProduct(product.id, { is_active: !product.is_active });
                                          loadShopData();
                                        }}
                                      >
                                        {product.is_active ? 'Visible' : 'Hidden'}
                                      </button>
                                    </td>
                                    <td data-label="Actions">
                                      <div className="cell-actions">
                                        <button className="btn-reschedule btn-tiny" onClick={() => openEditProduct(product)}>Edit</button>
                                        <button className="btn-decline btn-tiny" onClick={() => handleDeleteProduct(product)}>Delete</button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              </>
            )}

            {/* ==================== PROMOS ==================== */}
            {tab === 'promos' && (
              <>
                <div className="addto-section-header">
                  <h2 className="addto-section-title">🎉 Promotions</h2>
                  <button className="btn-add-small" onClick={openAddPromo}>+ Add Promotion</button>
                </div>
                {promosLoading ? (
                  <div className="no-bookings"><p>Loading...</p></div>
                ) : promotions.length === 0 ? (
                  <p className="empty-note">No promotions yet — click "+ Add Promotion" to create one</p>
                ) : (
                  promotions.map(promo => (
                    <div key={promo.id} className={`booking-card ${promo.is_active ? 'approved' : ''}`}>
                      <div className="card-header">
                        <h3 className="client-name">{promo.title}</h3>
                        <div className="card-header-controls">
                          <span className="status-pill status-pill--neutral">
                            {promo.type === 'section' ? '🖼 Section' : '📢 Banner'}
                          </span>
                          <button
                            type="button"
                            className={`pill-toggle ${promo.is_active ? 'status-pill approved' : 'status-pill pending'}`}
                            aria-pressed={!!promo.is_active}
                            onClick={() => handleTogglePromo(promo)}
                          >
                            {promo.is_active ? '✓ Live' : '○ Hidden'}
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        {promo.type === 'section' && promo.image_url && (
                          <div className="info-row">
                            <span className="info-label">Image:</span>
                            <img src={promo.image_url} alt="Promo" className="promo-thumb" />
                          </div>
                        )}
                        <div className="info-row">
                          <span className="info-label">Message:</span>
                          <span className="info-value">{promo.message}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Colour:</span>
                          <span className="info-value swatch-row">
                            <span className="swatch" style={{ background: promo.bg_color }} />
                            {promo.bg_color}
                          </span>
                        </div>
                      </div>
                      <div className="card-actions">
                        <button className="btn-reschedule" onClick={() => openEditPromo(promo)}>✏️ Edit</button>
                        <button className="btn-decline" onClick={() => handleDeletePromo(promo)}>🗑️ Delete</button>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {/* ==================== STOCK ==================== */}
            {tab === 'stock' && (
              <>
                <div className="addto-section-header">
                  <h2 className="addto-section-title">📦 Stock Levels</h2>
                  {Object.keys(stockEdits).filter(id => stockEdits[id] !== '' && stockEdits[id] !== undefined).length > 0 && (
                    <button
                      className="btn-approve btn-tiny"
                      onClick={async () => {
                        const edits = Object.entries(stockEdits).filter(([, v]) => v !== '' && v !== undefined);
                        if (edits.length === 0) return;
                        try {
                          await Promise.all(edits.map(([id, val]) => supabaseService.updateProductStock(id, val)));
                          setStockEdits({});
                          loadStockData();
                        } catch (err) {
                          alert('Failed to update some stock values.');
                        }
                      }}
                    >
                      Save All ({Object.keys(stockEdits).filter(id => stockEdits[id] !== '' && stockEdits[id] !== undefined).length})
                    </button>
                  )}
                </div>
                <p className="tab-intro">
                  Set stock quantities per product. Leave blank to mark as untracked (no stock limit shown to customers). Stock auto-deducts when you mark an order as fulfilled.
                </p>
                {stockProducts.some(p => p.stock !== null) && (
                  <div className="stock-summary">
                    {(() => {
                      const tracked = stockProducts.filter(p => p.stock !== null);
                      const outOfStock = tracked.filter(p => p.stock <= 0);
                      const lowStock = tracked.filter(p => p.stock > 0 && p.stock <= 5);
                      return (
                        <>
                          <span className={`stock-chip ${outOfStock.length > 0 ? 'bad' : 'good'}`}>
                            🚫 Out of stock: {outOfStock.length}
                          </span>
                          <span className={`stock-chip ${lowStock.length > 0 ? 'warn' : 'good'}`}>
                            ⚠️ Low stock (≤5): {lowStock.length}
                          </span>
                          <span className="stock-chip good">
                            ✓ Tracked: {tracked.length}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                )}
                <input
                  type="search"
                  aria-label="Search products"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="admin-input admin-input--search"
                />
                {stockLoading ? (
                  <div className="no-bookings"><p>Loading...</p></div>
                ) : stockProducts.length === 0 ? (
                  <p className="empty-note">No products yet — add products in the "Add to" tab first.</p>
                ) : (
                  <div className="cat-list">
                    {Object.entries(stockProducts.filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase())).reduce((acc, p) => {
                      if (!acc[p.category]) acc[p.category] = [];
                      acc[p.category].push(p);
                      return acc;
                    }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                      <div key={category} className="cat-group">
                        <button
                          type="button"
                          className={`cat-toggle ${expandedCats['stock_' + category] ? 'open' : ''}`}
                          aria-expanded={!!(expandedCats['stock_' + category] || productSearch)}
                          onClick={() => toggleCat('stock_' + category)}
                        >
                          <span>{category} ({items.length})</span>
                          <span className="cat-chevron" aria-hidden="true">{(expandedCats['stock_' + category] || productSearch) ? '▼' : '▶'}</span>
                        </button>
                        {(expandedCats['stock_' + category] || productSearch) && (
                          <div className="products-table-wrap">
                            <table className="products-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th className="col-center">Status</th>
                                  <th className="col-center">Current</th>
                                  <th className="col-center">Set Stock</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map(product => {
                                  const editVal = stockEdits[product.id] !== undefined ? stockEdits[product.id] : '';
                                  const isOutOfStock = product.stock !== null && product.stock <= 0;
                                  const isLow = product.stock !== null && product.stock > 0 && product.stock <= 3;
                                  return (
                                    <tr key={product.id}>
                                      <td data-label="Product" className="cell-name">{product.name}</td>
                                      <td data-label="Status" className="col-center">
                                        {product.stock === null ? (
                                          <span className="stock-untracked">Untracked</span>
                                        ) : isOutOfStock ? (
                                          <span className="status-pill pending">Out of stock</span>
                                        ) : isLow ? (
                                          <span className="status-pill low">Low — {product.stock} left</span>
                                        ) : (
                                          <span className="status-pill approved">In stock</span>
                                        )}
                                      </td>
                                      <td data-label="Current" className={`col-center cell-stock-count ${isOutOfStock ? 'out' : ''}`}>
                                        {product.stock === null ? '—' : product.stock}
                                      </td>
                                      <td data-label="Set stock" className="col-center">
                                        <div className="stock-controls">
                                          <button
                                            type="button"
                                            className="stepper-btn minus"
                                            aria-label={`Decrease stock for ${product.name}`}
                                            onClick={() => {
                                              const current = editVal !== '' ? Number(editVal) : (product.stock ?? 0);
                                              setStockEdits(prev => ({ ...prev, [product.id]: String(Math.max(0, current - 1)) }));
                                            }}
                                          >−</button>
                                          <input
                                            type="number"
                                            min="0"
                                            inputMode="numeric"
                                            placeholder="qty"
                                            aria-label={`Stock quantity for ${product.name}`}
                                            value={editVal}
                                            onChange={e => setStockEdits(prev => ({ ...prev, [product.id]: e.target.value }))}
                                            className="admin-input admin-input--qty"
                                          />
                                          <button
                                            type="button"
                                            className="stepper-btn plus"
                                            aria-label={`Increase stock for ${product.name}`}
                                            onClick={() => {
                                              const current = editVal !== '' ? Number(editVal) : (product.stock ?? 0);
                                              setStockEdits(prev => ({ ...prev, [product.id]: String(current + 1) }));
                                            }}
                                          >+</button>
                                          <button
                                            className="btn-approve btn-tiny"
                                            disabled={editVal === '' || editVal === undefined}
                                            onClick={async () => {
                                              try {
                                                await supabaseService.updateProductStock(product.id, editVal);
                                                setStockEdits(prev => { const n = { ...prev }; delete n[product.id]; return n; });
                                                loadStockData();
                                              } catch (err) {
                                                alert('Failed to update stock.');
                                              }
                                            }}
                                          >
                                            Set
                                          </button>
                                          {product.stock !== null && (
                                            <button
                                              className="btn-reschedule btn-tiny"
                                              aria-label={`Remove stock tracking for ${product.name}`}
                                              onClick={async () => {
                                                if (!window.confirm(`Remove stock tracking for "${product.name}"?`)) return;
                                                try {
                                                  await supabaseService.updateProductStock(product.id, null);
                                                  loadStockData();
                                                } catch (err) {
                                                  alert('Failed to update stock.');
                                                }
                                              }}
                                            >
                                              Untrack
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ==================== EMAIL BLAST ==================== */}
            {tab === 'email' && (
              <>
                <div className="addto-section-header">
                  <h2 className="addto-section-title">✉️ Email Blast</h2>
                </div>
                <p className="tab-intro">
                  Send a branded email to your customers. Select your audience, compose your message, preview, and send.
                </p>

                {/* Audience picker */}
                <div className="admin-form">
                  <div className="admin-field">
                    <span className="admin-field-label">Audience</span>
                    <div className="chip-row">
                      {[{ key: 'all', label: 'All Customers' }, { key: 'bookings', label: 'Booking Customers' }, { key: 'orders', label: 'Shop Customers' }].map(opt => (
                        <button
                          key={opt.key}
                          type="button"
                          aria-pressed={emailBlast.audience === opt.key}
                          className={`pill-toggle ${emailBlast.audience === opt.key ? 'status-pill approved' : 'status-pill pending'}`}
                          onClick={() => {
                            setEmailBlast(prev => ({ ...prev, audience: opt.key }));
                            loadEmailData(opt.key);
                          }}
                        >{opt.label}</button>
                      ))}
                    </div>
                    <span className="admin-field-note">
                      {emailsLoading ? 'Loading...' : `${customerEmails.length} recipient${customerEmails.length !== 1 ? 's' : ''}`}
                    </span>
                  </div>

                  <div className="admin-field">
                    <label className="admin-field-label" htmlFor="blast-subject">Subject Line *</label>
                    <input
                      id="blast-subject"
                      type="text"
                      placeholder="e.g. New Products Available!"
                      value={emailBlast.subject}
                      onChange={e => setEmailBlast(prev => ({ ...prev, subject: e.target.value }))}
                      className="admin-input"
                    />
                  </div>

                  <div className="admin-field">
                    <span className="admin-field-label">Email Image (optional)</span>
                    {emailBlast.imageUrl && (
                      <div className="thumb-wrap">
                        <img src={emailBlast.imageUrl} alt="Email" className="thumb-preview" />
                        <button
                          type="button"
                          className="thumb-remove"
                          aria-label="Remove email image"
                          onClick={() => setEmailBlast(prev => ({ ...prev, imageUrl: '' }))}
                        >×</button>
                      </div>
                    )}
                    <div className="upload-row">
                      <label className={`upload-drop ${emailImageUploading ? 'busy' : ''}`}>
                        {emailImageUploading ? 'Uploading…' : '↑ Upload from computer'}
                        <input type="file" accept="image/*" className="visually-hidden" disabled={emailImageUploading} onChange={e => { if (e.target.files[0]) uploadEmailImage(e.target.files[0]); e.target.value = ''; }} />
                      </label>
                      <button type="button" className="btn-outline"
                        onClick={() => { const next = !showEmailGallery; setShowEmailGallery(next); if (next) loadBannerImages(); }}
                      >
                        {showEmailGallery ? 'Hide gallery' : 'Browse uploads'}
                      </button>
                    </div>
                    {showEmailGallery && (
                      <div className="gallery-strip">
                        {bannerImages.length === 0 ? (
                          <span className="empty-note">No images uploaded yet.</span>
                        ) : bannerImages.map(img => (
                          <button
                            key={img.name}
                            type="button"
                            title={img.name}
                            aria-label={`Use image ${img.name}`}
                            className={`gallery-item ${emailBlast.imageUrl === img.url ? 'selected' : ''}`}
                            onClick={() => { setEmailBlast(prev => ({ ...prev, imageUrl: img.url })); setShowEmailGallery(false); }}
                          >
                            <img src={img.url} alt="" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="admin-field">
                    <label className="admin-field-label" htmlFor="blast-body">Message *</label>
                    <textarea
                      id="blast-body"
                      rows="8"
                      className="reschedule-textarea"
                      placeholder="Write your message here. Each paragraph will be nicely formatted in the email."
                      value={emailBlast.body}
                      onChange={e => setEmailBlast(prev => ({ ...prev, body: e.target.value }))}
                    />
                  </div>

                  <div className="button-row">
                    <button
                      className="btn-reschedule btn-stack"
                      onClick={() => setEmailBlast(prev => ({ ...prev, preview: !prev.preview }))}
                    >{emailBlast.preview ? 'Hide Preview' : 'Preview Email'}</button>
                    <button
                      className="btn-approve btn-stack"
                      disabled={!emailBlast.subject || !emailBlast.body || customerEmails.length === 0 || emailBlast.sending || isBusy}
                      onClick={async () => {
                        // Claim the lock BEFORE the confirm dialog. window.confirm
                        // blocks the main thread, so React never re-renders with
                        // the disabled state until it closes — a double-tap queued
                        // behind it would otherwise send the whole blast twice, to
                        // every customer.
                        if (!beginAction('blast')) return;
                        if (!window.confirm(`Send this email to ${customerEmails.length} customer${customerEmails.length !== 1 ? 's' : ''}?`)) {
                          endAction();
                          return;
                        }
                        setEmailBlast(prev => ({ ...prev, sending: true }));
                        try {
                          const imgHtml = emailBlast.imageUrl ? `<img src="${emailBlast.imageUrl}" alt="" style="width:100%;max-width:540px;border-radius:8px;margin:0 0 20px;" />` : '';
                          const bodyHtml = emailBlast.body.split('\n\n').map(p => `<p style="color:#333;font-size:15px;line-height:1.6;margin:0 0 16px;">${p.replace(/\n/g, '<br>')}</p>`).join('');
                          const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;font-family:'Helvetica Neue',Arial,sans-serif;background:#f8f9fa;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f8f9fa;"><tr><td align="center" style="padding:20px;"><table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background:white;border-radius:12px;overflow:hidden;box-shadow:0 2px 10px rgba(0,0,0,0.08);max-width:600px;width:100%;"><tr><td style="background:#6B9E7A;padding:25px;text-align:center;"><h1 style="color:white;margin:0;font-size:22px;font-weight:600;letter-spacing:1px;">Beauty by Viktoria</h1><p style="color:rgba(255,255,255,0.85);margin:8px 0 0;font-size:14px;">Beauty Therapy</p></td></tr><tr><td style="padding:30px 25px;">${imgHtml}${bodyHtml}</td></tr><tr><td style="padding:20px 25px;text-align:center;border-top:1px solid #eee;"><p style="color:#555;font-size:13px;margin:0 0 4px;"><a href="mailto:viktoriashouseofbeauty@gmail.com" style="color:#6B9E7A;text-decoration:none;">viktoriashouseofbeauty@gmail.com</a></p><p style="color:#555;font-size:13px;margin:0 0 12px;"><a href="tel:021881498" style="color:#6B9E7A;text-decoration:none;">021 881 498</a></p><p style="color:#999;font-size:11px;margin:0;">Beauty by Viktoria &bull; Richmond, New Zealand</p></td></tr></table></td></tr></table></body></html>`;
                          const recipients = customerEmails.map(c => c.email);
                          const result = await supabaseService.sendEmailBlast(emailBlast.subject, html, recipients);
                          alert(`Email sent to ${result.sent} customer${result.sent !== 1 ? 's' : ''}${result.failed > 0 ? ` (${result.failed} failed)` : ''}`);
                          setEmailBlast(prev => ({ ...prev, sending: false }));
                        } catch (err) {
                          alert('Failed to send email blast: ' + err.message);
                          setEmailBlast(prev => ({ ...prev, sending: false }));
                        } finally {
                          endAction();
                        }
                      }}
                    >{emailBlast.sending ? 'Sending...' : `Send to ${customerEmails.length} Customer${customerEmails.length !== 1 ? 's' : ''}`}</button>
                  </div>
                </div>

                {/* Preview. The real email is a 600px table; this mirrors it but is
                    capped by the column it sits in, so it never widens the page. */}
                {emailBlast.preview && (
                  <div className="email-preview">
                    <div className="email-preview-head">
                      <h2>Beauty by Viktoria</h2>
                      <p>Beauty Therapy</p>
                    </div>
                    <div className="email-preview-body">
                      {emailBlast.imageUrl && <img src={emailBlast.imageUrl} alt="" className="email-preview-img" />}
                      {emailBlast.body ? emailBlast.body.split('\n\n').map((p, i) => (
                        <p key={i}>{p}</p>
                      )) : <p className="email-preview-placeholder">Your message will appear here...</p>}
                    </div>
                    <div className="email-preview-foot">
                      <p>Beauty by Viktoria &bull; Richmond, New Zealand</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ==================== STATS ==================== */}
            {tab === 'stats' && (() => {
              const parsePrice = (p) => { const n = parseFloat((p || '').toString().replace(/[^0-9.]/g, '')); return isNaN(n) ? 0 : n; };
              const monthBookings = statsData.bookings;
              const monthOrders = statsData.orders;

              // Week start (Monday) helper
              const getWeekStart = (dateStr) => {
                const d = new Date(dateStr + 'T00:00:00');
                const day = d.getDay();
                const diff = d.getDate() - day + (day === 0 ? -6 : 1);
                const monday = new Date(d.setDate(diff));
                // toISOString() would give the UTC date. In NZ (UTC+12/13) that is
                // the Sunday, so every week was keyed and labelled a day early.
                return toISODate(monday);
              };

              // Day of week (0=Sun, 1=Mon ... 6=Sat)
              const getDow = (dateStr) => new Date(dateStr + 'T00:00:00').getDay();
              const summersetDays = [2, 3, 4]; // Tue, Wed, Thu

              const summersetBookings = monthBookings.filter(b => summersetDays.includes(getDow(b.date)));
              const nonSummersetBookings = monthBookings.filter(b => !summersetDays.includes(getDow(b.date)));

              const summersetRevenue = summersetBookings.reduce((sum, b) => sum + parsePrice(b.total_price), 0);
              const nonSummersetRevenue = nonSummersetBookings.reduce((sum, b) => sum + parsePrice(b.total_price), 0);
              const totalBookingRevenue = summersetRevenue + nonSummersetRevenue;

              const orderRevenue = monthOrders.reduce((sum, o) => {
                return sum + (o.shop_order_items || []).reduce((s, i) => s + parsePrice(i.price) * (i.quantity || 1), 0);
              }, 0);
              const productsSold = monthOrders.reduce((sum, o) => sum + (o.shop_order_items || []).reduce((s, i) => s + (i.quantity || 1), 0), 0);

              const pct = parseFloat(summersetPercent) || 0;
              const summersetCut = summersetRevenue * (pct / 100);
              const netRevenue = totalBookingRevenue - summersetCut + orderRevenue;

              // Weekly breakdown
              const weeklyMap = {};
              monthBookings.forEach(b => {
                const week = getWeekStart(b.date);
                if (!weeklyMap[week]) weeklyMap[week] = { summerset: 0, other: 0, count: 0 };
                const price = parsePrice(b.total_price);
                weeklyMap[week].count++;
                if (summersetDays.includes(getDow(b.date))) {
                  weeklyMap[week].summerset += price;
                } else {
                  weeklyMap[week].other += price;
                }
              });

              // Get current week's bookings count
              const now = new Date();
              const currentWeekStart = getWeekStart(toISODate(now));
              const weekBookings = monthBookings.filter(b => getWeekStart(b.date) === currentWeekStart).length;

              return (
                <>
                  <div className="addto-section-header">
                    <h2 className="addto-section-title">📊 Stats & Revenue</h2>
                    <input
                      type="month"
                      aria-label="Month to show"
                      value={statsMonth}
                      onChange={e => setStatsMonth(e.target.value)}
                      className="admin-input admin-input--inline"
                    />
                  </div>

                  {statsLoading ? (
                    <div className="no-bookings"><p>Loading...</p></div>
                  ) : (
                    <>
                      {/* Overview cards */}
                      <div className="stat-grid">
                        <div className="stat-card">
                          <p className="stat-label">This Week</p>
                          <p className="stat-value">{weekBookings}</p>
                          <p className="stat-label">bookings</p>
                        </div>
                        <div className="stat-card">
                          <p className="stat-label">This Month</p>
                          <p className="stat-value">{monthBookings.length}</p>
                          <p className="stat-label">bookings</p>
                        </div>
                        <div className="stat-card">
                          <p className="stat-label">Booking Revenue</p>
                          <p className="stat-value">${totalBookingRevenue.toFixed(0)}</p>
                          <p className="stat-label">this month</p>
                        </div>
                        <div className="stat-card">
                          <p className="stat-label">Products Sold</p>
                          <p className="stat-value">{productsSold}</p>
                          <p className="stat-label">${orderRevenue.toFixed(0)} revenue</p>
                        </div>
                      </div>

                      {/* Summerset section */}
                      <div className="stats-panel">
                        <h3 className="stats-panel-title">Summerset (Tue / Wed / Thu)</h3>
                        <div className="stats-figures">
                          <div className="stats-figure">
                            <span className="stats-figure-label">Bookings: </span>
                            <strong>{summersetBookings.length}</strong>
                          </div>
                          <div className="stats-figure">
                            <span className="stats-figure-label">Revenue: </span>
                            <strong className="money-green">${summersetRevenue.toFixed(2)}</strong>
                          </div>
                          <div className="stats-figure stats-figure--input">
                            <label className="stats-figure-label" htmlFor="summerset-pct">Summerset %: </label>
                            <input
                              id="summerset-pct"
                              type="number"
                              min="0"
                              max="100"
                              inputMode="numeric"
                              placeholder="%"
                              value={summersetPercent}
                              onChange={e => setSummersetPercent(e.target.value)}
                              className="admin-input admin-input--qty"
                            />
                          </div>
                          {pct > 0 && (
                            <>
                              <div className="stats-figure">
                                <span className="stats-figure-label">Summerset cut: </span>
                                <strong className="money-red">-${summersetCut.toFixed(2)}</strong>
                              </div>
                              <div className="stats-figure">
                                <span className="stats-figure-label">Take-home: </span>
                                <strong className="money-deep">${(summersetRevenue - summersetCut).toFixed(2)}</strong>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Non-Summerset section */}
                      <div className="stats-panel">
                        <h3 className="stats-panel-title">Other Days (Mon / Fri / Sat / Sun)</h3>
                        <div className="stats-figures">
                          <div className="stats-figure">
                            <span className="stats-figure-label">Bookings: </span>
                            <strong>{nonSummersetBookings.length}</strong>
                          </div>
                          <div className="stats-figure">
                            <span className="stats-figure-label">Revenue: </span>
                            <strong className="money-green">${nonSummersetRevenue.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>

                      {/* Weekly breakdown table */}
                      {Object.keys(weeklyMap).length > 0 && (
                        <div className="stats-panel">
                          <h3 className="stats-panel-title">Weekly Breakdown</h3>
                          <div className="products-table-wrap">
                            <table className="products-table">
                              <thead>
                                <tr>
                                  <th>Week Starting</th>
                                  <th className="col-center">Bookings</th>
                                  <th className="col-right">Summerset</th>
                                  <th className="col-right">Other</th>
                                  <th className="col-right">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(weeklyMap).sort(([a], [b]) => a.localeCompare(b)).map(([week, data]) => (
                                  <tr key={week}>
                                    <td data-label="Week starting" className="cell-name">{new Date(week + 'T00:00:00').toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}</td>
                                    <td data-label="Bookings" className="col-center">{data.count}</td>
                                    <td data-label="Summerset" className="col-right cell-muted">${data.summerset.toFixed(0)}</td>
                                    <td data-label="Other" className="col-right cell-muted">${data.other.toFixed(0)}</td>
                                    <td data-label="Total" className="col-right cell-total">${(data.summerset + data.other).toFixed(0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Monthly totals */}
                      <div className="stats-panel stats-panel--summary">
                        <h3 className="stats-panel-title">Monthly Summary</h3>
                        <div className="summary-rows">
                          <div className="summary-row">
                            <span>Booking revenue</span>
                            <strong>${totalBookingRevenue.toFixed(2)}</strong>
                          </div>
                          <div className="summary-row">
                            <span>Shop order revenue</span>
                            <strong>${orderRevenue.toFixed(2)}</strong>
                          </div>
                          {pct > 0 && (
                            <div className="summary-row summary-row--deduct">
                              <span>Summerset deduction ({pct}%)</span>
                              <strong>-${summersetCut.toFixed(2)}</strong>
                            </div>
                          )}
                          <div className="summary-row summary-row--total">
                            <strong>Net Revenue</strong>
                            <strong>${netRevenue.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              );
            })()}

          </div>
        </div>
      </div>

      {/* ==================== APPROVAL MODAL ==================== */}
      {approvalModal.isOpen && (
        <div className="modal-overlay" onClick={isBusy ? undefined : closeApprovalModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Set Appointment Time</h2>
              <button className="modal-close" onClick={closeApprovalModal} disabled={isBusy}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-client-name">
                <strong>{approvalModal.booking?.name}</strong>
              </p>
              <p className="modal-info">
                {formatDate(approvalModal.booking?.date)}
              </p>
              <p className="modal-info available-window">
                Client is available: <strong>{formatTimeRange(
                  approvalModal.booking?.time_range_start,
                  approvalModal.booking?.time_range_end
                )}</strong>
              </p>

              {/* Client Type Selector */}
              <div className="client-type-selector">
                <label className="selector-label">Client type (for email template):</label>
                <div className="client-type-options">
                  <button
                    type="button"
                    className={`client-type-btn ${approvalModal.clientType === 'returning' ? 'active returning' : ''}`}
                    onClick={() => setApprovalModal(prev => ({ ...prev, clientType: 'returning' }))}
                  >
                    🔁 Returning Client
                  </button>
                  <button
                    type="button"
                    className={`client-type-btn ${approvalModal.clientType === 'new' ? 'active new' : ''}`}
                    onClick={() => setApprovalModal(prev => ({ ...prev, clientType: 'new' }))}
                  >
                    ⭐ New Client
                  </button>
                </div>
              </div>

              {/* What's already booked that day */}
              <div className="modal-calendar-section">
                <div className="modal-calendar-header">
                  <span className="modal-calendar-label">
                    📅 Booked on {formatDate(approvalModal.booking?.date)}
                  </span>
                </div>
                {renderDaySchedule(approvalModal.booking?.date)}
              </div>

              <div className="modal-time-picker">
                <label htmlFor="appointment-time">Choose appointment time (15-min intervals):</label>
                <select
                  id="appointment-time"
                  value={approvalModal.selectedTime}
                  onChange={(e) => setApprovalModal(prev => ({
                    ...prev,
                    selectedTime: e.target.value
                  }))}
                  className="time-select"
                >
                  <option value="">-- Select time --</option>
                  {timeOptions.map(time => (
                    <option key={time} value={time}>
                      {formatTime(time)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-modal-approve" onClick={handleApproveWithTime} disabled={isBusy}>
                {actionBusy === 'approve' ? 'Confirming…' : '✓ Confirm Booking'}
              </button>
              <button className="btn-modal-cancel" onClick={closeApprovalModal} disabled={isBusy}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MOVE MODAL ==================== */}
      {moveModal.isOpen && (() => {
        const moveTreatments = (moveModal.booking?.booking_treatments || [])
          .map(bt => bt.treatment_name)
          .filter(Boolean)
          .join(' · ');

        return (
          <div className="modal-overlay" onClick={moveSending ? undefined : closeMoveModal}>
            <div className="modal-content reschedule-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Move this booking</h2>
                <button className="modal-close" onClick={closeMoveModal} disabled={moveSending}>×</button>
              </div>

              <div className="modal-body">
                <p className="modal-client-name">
                  <strong>{moveModal.booking?.name}</strong>
                </p>
                <p className="reschedule-treatments">
                  {moveTreatments || 'No treatments recorded'}
                  <span className="reschedule-treatments-mins"> — {moveDuration} min</span>
                </p>

                {/* The time she is taking it away from, so she can see at a
                    glance that she has the right booking open. */}
                <div className="move-current">
                  <span className="move-current-label">Booked now</span>
                  <strong>
                    {formatDate(moveModal.booking?.date)}
                    {moveModal.booking?.time ? ` at ${formatTime(moveModal.booking?.time)}` : ''}
                  </strong>
                </div>

                {/* ── Where it's going ── */}
                <div className="reschedule-section">
                  <div className="reschedule-section-head">
                    <h3 className="reschedule-section-title">Move it to</h3>
                  </div>
                  <p className="reschedule-section-sub">
                    Your real free times that fit a {moveDuration} minute appointment.
                    Whatever you pick is taken the moment you confirm.
                  </p>

                  {moveNotice && (
                    <p className="reschedule-dropped">{moveNotice}</p>
                  )}

                  {/* A fortnight at a time, same as the public booking form */}
                  <div className="reschedule-nav">
                    <button
                      type="button"
                      className="reschedule-nav-btn"
                      onClick={() => shiftMovePage(-MOVE_DAYS_PER_PAGE)}
                      disabled={moveAtStart || moveSlotsState === 'loading'}
                      aria-label="Earlier dates"
                    >
                      <ChevronLeft size={18} strokeWidth={2} />
                    </button>
                    <span className="reschedule-nav-label">
                      {moveAtStart
                        ? 'Next 2 weeks'
                        : `From ${formatDate(toISODate(movePageStart))}`}
                    </span>
                    <button
                      type="button"
                      className="reschedule-nav-btn"
                      onClick={() => shiftMovePage(MOVE_DAYS_PER_PAGE)}
                      disabled={moveSlotsState === 'loading'}
                      aria-label="Later dates"
                    >
                      <ChevronRight size={18} strokeWidth={2} />
                    </button>
                  </div>

                  {moveSlotsState === 'loading' && (
                    <p className="reschedule-slots-msg">Finding your free times…</p>
                  )}

                  {moveSlotsState === 'error' && (
                    <div className="reschedule-slots-error">
                      <span>{moveSlotsError}</span>
                      <button
                        type="button"
                        className="btn-add-small"
                        onClick={() => setMoveReloadKey(k => k + 1)}
                      >
                        Try again
                      </button>
                      <span className="reschedule-hint">
                        Nothing has changed — the booking is still where it was.
                      </span>
                    </div>
                  )}

                  {moveSlotsState === 'ready' && moveDays.length === 0 && (
                    <p className="reschedule-slots-msg">
                      Nothing free in this fortnight for a {moveDuration} minute appointment.
                      Try the arrow above to look further ahead.
                    </p>
                  )}

                  {moveSlotsState === 'ready' && moveDays.map(day => {
                    const daySlots = moveSlotsByDay[day];
                    const dayOpen = moveOpenDay === day || moveDays.length <= 3;
                    return (
                      <div key={day} className="reschedule-day">
                        <button
                          type="button"
                          className={`reschedule-day-head ${dayOpen ? 'expanded' : ''}`}
                          onClick={() => setMoveOpenDay(dayOpen && moveOpenDay === day ? '' : day)}
                          aria-expanded={dayOpen}
                        >
                          <span className="reschedule-day-name">{formatDate(day)}</span>
                          <span className="reschedule-day-count">
                            {daySlots.length} time{daySlots.length === 1 ? '' : 's'}
                          </span>
                        </button>
                        {dayOpen && (
                          <>
                            {/* Once per day, not once per time */}
                            {daySlots[0].location_name && (
                              <p className="reschedule-day-location">
                                {daySlots[0].location_address || daySlots[0].location_name}
                              </p>
                            )}
                            <div className="reschedule-slot-grid">
                              {daySlots.map(slot => {
                                const active =
                                  moveSlot &&
                                  moveSlot.slot_date === slot.slot_date &&
                                  moveSlot.slot_time === slot.slot_time;
                                return (
                                  <button
                                    key={`${slot.slot_date}-${slot.slot_time}-${slot.location_name || ''}`}
                                    type="button"
                                    className={`reschedule-slot ${active ? 'selected' : ''}`}
                                    aria-pressed={!!active}
                                    onClick={() => {
                                      setMoveNotice('');
                                      setMoveSlot(active ? null : slot);
                                    }}
                                  >
                                    {formatTime(slot.slot_time)}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}

                  {moveSlot && (
                    <div className="reschedule-chosen">
                      <span className="reschedule-chosen-label">Moving to</span>
                      <strong>
                        {formatDate(moveSlot.slot_date)} at {formatTime(moveSlot.slot_time)}
                      </strong>
                      {moveSlot.location_address && (
                        <span className="reschedule-chosen-where">{moveSlot.location_address}</span>
                      )}
                      <button type="button" className="btn-add-small" onClick={() => setMoveSlot(null)}>
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Optional note ── */}
                <div className="reschedule-form">
                  <label htmlFor="move-note">
                    Add a note <span className="reschedule-optional">Optional</span>
                  </label>
                  <textarea
                    id="move-note"
                    value={moveModal.note}
                    onChange={(e) => setMoveModal(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="Sorry, something's come up that morning."
                    rows="3"
                    className="reschedule-textarea reschedule-textarea--short"
                  />
                  <p className="reschedule-hint">
                    Goes in the email in your own words. Leave it blank and the email still makes sense.
                  </p>
                </div>

                {/* ── What actually happens ── */}
                <div className="reschedule-outcome">
                  <span className="reschedule-outcome-title">What happens when you confirm</span>
                  <ul>
                    <li>
                      The appointment moves straight away and the new time is held. There is
                      nothing for {moveModal.booking?.name} to accept.
                    </li>
                    <li>
                      {moveModal.booking?.name} is emailed the new day and time
                      {moveModal.note.trim() ? ', along with your note' : ''}, with a link to
                      pick something else if it doesn't suit.
                    </li>
                    <li>
                      Your calendar entry moves with it — the old one goes, a new one takes its place.
                    </li>
                  </ul>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn-modal-approve"
                  onClick={handleMove}
                  disabled={isBusy || !moveSlot}
                >
                  {moveSending
                    ? 'Moving…'
                    : moveSlot
                      ? '✓ Move it and let them know'
                      : 'Pick a new time above'}
                </button>
                <button
                  className="btn-modal-cancel"
                  onClick={closeMoveModal}
                  disabled={moveSending}
                >
                  Leave it as it is
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ==================== VOUCHER REDEEM MODAL ====================
          The code is typed, never prefilled. Money comes off a real voucher
          here and there is no undo, so this is deliberately a step rather than
          a confirm — and typing the code means the client has it in front of
          her. The amount is optional and empty means all of it, so using a
          voucher up is still one thing to do, not two. */}
      {redeemModal.isOpen && (
        <div className="modal-overlay" onClick={isBusy ? undefined : closeRedeemModal}>
          <div className="modal-content reschedule-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark a voucher as used</h2>
              <button className="modal-close" onClick={closeRedeemModal} disabled={isBusy}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                Whichever voucher the code belongs to is the one this comes off
                — so type it off the card in front of you.
              </p>
              {/* Above the code, not under the note. The submit button sits in
                  .modal-actions, outside this scrolling body, so on a phone the
                  form is already taller than the body when she taps it — an
                  answer rendered last ("That voucher only has $115.00 left on
                  it") landed below the fold and nothing on screen changed, so
                  she'd tap again. Here it is next to the field she has to fix,
                  and scrolled to if the body happens to be scrolled past it.
                  'nearest' so an error already in view doesn't jump. */}
              {redeemModal.error && (
                <p
                  className="voucher-redeem-error"
                  ref={el => { el?.scrollIntoView({ block: 'nearest' }); }}
                >
                  {redeemModal.error}
                </p>
              )}
              {/* A real form, so the Go key on a phone keyboard submits it.
                  The button lives outside the modal body in .modal-actions, so
                  it reaches back in by id rather than being moved in here. */}
              <form
                id="voucher-redeem-form"
                className="reschedule-form"
                onSubmit={e => { e.preventDefault(); if (!isBusy && redeemModal.code.trim()) handleRedeemVoucher(); }}
              >
                <label htmlFor="voucher-redeem-code">Type the code on their voucher:</label>
                <input
                  id="voucher-redeem-code"
                  type="text"
                  className="voucher-code-input"
                  value={redeemModal.code}
                  onChange={e => setRedeemModal(prev => ({ ...prev, code: e.target.value, error: '' }))}
                  placeholder="BV-XXXX-XXXX"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck="false"
                  disabled={isBusy}
                />
                <p className="reschedule-hint">
                  Capitals, spaces and dashes don't matter — type it however it reads.
                </p>

                {/* Empty is the answer most of the time, so empty is the one
                    that needs no typing: it takes whatever is left on the
                    voucher. A number only when the treatment costs less than
                    the voucher is worth, and then the rest stays live. */}
                <label htmlFor="voucher-redeem-amount">How much is coming off it? (optional)</label>
                <input
                  id="voucher-redeem-amount"
                  type="number"
                  inputMode="decimal"
                  className="voucher-amount-input"
                  value={redeemModal.amount}
                  onChange={e => setRedeemModal(prev => ({ ...prev, amount: e.target.value, error: '' }))}
                  placeholder="Leave empty to use it all"
                  min="0"
                  step="0.01"
                  autoComplete="off"
                  disabled={isBusy}
                />
                <p className="reschedule-hint">
                  In dollars. Leave it empty and I'll take whatever is left on the
                  voucher, which is usually what you want. Put a number in and the
                  rest stays on it for next time.
                </p>

                <label htmlFor="voucher-redeem-note">What it went towards (optional):</label>
                <textarea
                  id="voucher-redeem-note"
                  value={redeemModal.note}
                  onChange={e => setRedeemModal(prev => ({ ...prev, note: e.target.value }))}
                  rows="2"
                  className="reschedule-textarea"
                  placeholder="e.g. Signature facial, 18 Aug"
                  disabled={isBusy}
                />
              </form>
            </div>
            <div className="modal-actions">
              <button
                type="submit"
                form="voucher-redeem-form"
                className="btn-modal-approve"
                disabled={isBusy || !redeemModal.code.trim()}
              >
                {/* The button says which of the two things is about to happen,
                    so an amount typed by mistake is caught before she taps. */}
                {actionBusy === 'voucher-redeem'
                  ? 'Marking…'
                  : redeemModal.amount.trim()
                    ? `✓ Take $${formatAmount(redeemModal.amount.trim())} off it`
                    : '✓ Use the whole voucher'}
              </button>
              <button type="button" className="btn-modal-cancel" onClick={closeRedeemModal} disabled={isBusy}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== ORDER RESPONSE MODAL ==================== */}
      {orderModal.isOpen && (
        <div className="modal-overlay" onClick={isBusy ? undefined : closeOrderModal}>
          <div className="modal-content reschedule-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Respond to Order</h2>
              <button className="modal-close" onClick={closeOrderModal} disabled={isBusy}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-client-name"><strong>{orderModal.order?.customer_name}</strong></p>
              <p className="modal-info">{orderModal.order?.email} — {orderModal.order?.fulfillment === 'pickup' ? 'Pickup' : 'Delivery'}</p>
              <div className="reschedule-form">
                <label htmlFor="order-response-msg">Message to customer:</label>
                <textarea
                  id="order-response-msg"
                  value={orderModal.message}
                  onChange={e => setOrderModal(prev => ({ ...prev, message: e.target.value }))}
                  rows="8"
                  className="reschedule-textarea"
                />
                <p className="reschedule-hint">This message will be emailed to {orderModal.order?.customer_name}.</p>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-modal-approve" onClick={handleOrderResponse} disabled={isBusy}>
                {actionBusy === 'order-response' ? 'Sending…' : '📧 Send Response'}
              </button>
              <button className="btn-modal-cancel" onClick={closeOrderModal} disabled={isBusy}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PRODUCT MODAL ==================== */}
      {productModal.isOpen && (
        <div className="modal-overlay" onClick={closeProductModal}>
          <div className="modal-content reschedule-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{productModal.editing ? 'Edit Product' : 'Add Product'}</h2>
              <button className="modal-close" onClick={closeProductModal}>×</button>
            </div>
            <div className="modal-body modal-body--stack">
              {[
                { label: 'Product Name *', key: 'name', placeholder: 'e.g. Daily Microfoliant' },
                { label: 'Description', key: 'description', placeholder: 'Short product description', multi: true },
                { label: 'Display Order', key: 'display_order', type: 'number' },
              ].map(({ label, key, placeholder, multi, type }) => (
                <div key={key} className="admin-field">
                  <label className="admin-field-label" htmlFor={`product-${key}`}>{label}</label>
                  {multi ? (
                    <textarea
                      id={`product-${key}`}
                      rows="3"
                      className="reschedule-textarea reschedule-textarea--short"
                      value={productModal.form[key]}
                      placeholder={placeholder}
                      onChange={e => setProductModal(prev => ({ ...prev, form: { ...prev.form, [key]: e.target.value } }))}
                    />
                  ) : (
                    <input
                      id={`product-${key}`}
                      type={type || 'text'}
                      className="admin-input"
                      value={productModal.form[key]}
                      placeholder={placeholder}
                      onChange={e => setProductModal(prev => ({ ...prev, form: { ...prev.form, [key]: type === 'number' ? Number(e.target.value) : e.target.value } }))}
                    />
                  )}
                </div>
              ))}

              {/* Product image upload */}
              <div className="admin-field">
                <span className="admin-field-label">Product Image</span>
                {productModal.form.image_url && (
                  <div className="thumb-wrap">
                    <img src={productModal.form.image_url} alt="Product" className="thumb-preview" />
                    <button
                      type="button"
                      className="thumb-remove"
                      aria-label="Remove product image"
                      onClick={() => setProductModal(prev => ({ ...prev, form: { ...prev.form, image_url: '' } }))}
                    >×</button>
                  </div>
                )}
                <div className="upload-row">
                  <label className={`upload-drop ${productImageUploading ? 'busy' : ''}`}>
                    {productImageUploading ? 'Uploading…' : '↑ Upload from computer'}
                    <input type="file" accept="image/*" className="visually-hidden" disabled={productImageUploading} onChange={e => { if (e.target.files[0]) uploadProductImage(e.target.files[0]); e.target.value = ''; }} />
                  </label>
                  <button type="button" className="btn-outline"
                    onClick={() => { const next = !showProductGallery; setShowProductGallery(next); if (next) loadBannerImages(); }}
                  >
                    {showProductGallery ? 'Hide gallery' : 'Browse uploads'}
                  </button>
                </div>
                {showProductGallery && (
                  <div className="gallery-strip">
                    {bannerImages.length === 0 ? (
                      <span className="empty-note">No images uploaded yet.</span>
                    ) : bannerImages.map(img => (
                      <button
                        key={img.name}
                        type="button"
                        title={img.name}
                        aria-label={`Use image ${img.name}`}
                        className={`gallery-item ${productModal.form.image_url === img.url ? 'selected' : ''}`}
                        onClick={() => { setProductModal(prev => ({ ...prev, form: { ...prev.form, image_url: img.url } })); setShowProductGallery(false); }}
                      >
                        <img src={img.url} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Category dropdown */}
              <div className="admin-field">
                <label className="admin-field-label" htmlFor="product-category">Category *</label>
                {productModal.form.category === '__new__' || (productModal.form.category && !existingCategories.includes(productModal.form.category) && productModal.editing) ? (
                  <div className="field-row">
                    <input
                      id="product-category"
                      type="text"
                      className="admin-input"
                      value={productModal.form.category === '__new__' ? '' : productModal.form.category}
                      placeholder="Enter new category name"
                      autoFocus
                      onChange={e => setProductModal(prev => ({ ...prev, form: { ...prev.form, category: e.target.value } }))}
                    />
                    {existingCategories.length > 0 && (
                      <button
                        type="button"
                        className="btn-plain"
                        onClick={() => setProductModal(prev => ({ ...prev, form: { ...prev.form, category: existingCategories[0] } }))}
                      >Cancel</button>
                    )}
                  </div>
                ) : (
                  <select
                    id="product-category"
                    className="admin-input admin-select"
                    value={productModal.form.category}
                    onChange={e => setProductModal(prev => ({ ...prev, form: { ...prev.form, category: e.target.value } }))}
                  >
                    {existingCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__new__">+ New category...</option>
                  </select>
                )}
              </div>

              {/* Price field with $ prefix */}
              <div className="admin-field">
                <label className="admin-field-label" htmlFor="product-price">Price *</label>
                <input
                  id="product-price"
                  type="text"
                  className="admin-input"
                  value={productModal.form.price}
                  placeholder="$45"
                  onChange={e => {
                    let val = e.target.value;
                    if (!val.startsWith('$')) val = '$' + val.replace('$', '');
                    setProductModal(prev => ({ ...prev, form: { ...prev.form, price: val } }));
                  }}
                />
              </div>

              <label className="admin-check">
                <input
                  type="checkbox"
                  checked={productModal.form.is_active}
                  onChange={e => setProductModal(prev => ({ ...prev, form: { ...prev.form, is_active: e.target.checked } }))}
                />
                Visible on shop page
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-modal-approve" onClick={handleSaveProduct}>
                {productModal.editing ? '✓ Save Changes' : '+ Add Product'}
              </button>
              <button className="btn-modal-cancel" onClick={closeProductModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PROMO MODAL ==================== */}
      {promoModal.isOpen && (
        <div className="modal-overlay" onClick={closePromoModal}>
          <div className="modal-content reschedule-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{promoModal.editing ? 'Edit Promotion' : 'Add Promotion'}</h2>
              <button className="modal-close" onClick={closePromoModal}>×</button>
            </div>
            <div className="modal-body modal-body--stack">
              {/* Type selector */}
              <div className="admin-field">
                <span className="admin-field-label">Promo type *</span>
                <div className="promo-type-row">
                  {[{ val: 'banner', label: '📢 Banner', desc: 'Thin strip at the top of every page' }, { val: 'section', label: '🖼 Section', desc: 'Photo + text card on Home & Shop pages' }].map(({ val, label, desc }) => (
                    <label
                      key={val}
                      className={`promo-type-option ${promoModal.form.type === val ? 'selected' : ''}`}
                    >
                      <input type="radio" name="promo-type" value={val} checked={promoModal.form.type === val} onChange={() => setPromoModal(prev => ({ ...prev, form: { ...prev.form, type: val } }))} className="visually-hidden" />
                      <span className="promo-type-label">{label}</span>
                      <span className="promo-type-desc">{desc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="admin-field">
                <label className="admin-field-label" htmlFor="promo-title">Title *</label>
                <input
                  id="promo-title"
                  type="text"
                  className="admin-input"
                  placeholder="e.g. 🌿 Spring Special!"
                  value={promoModal.form.title}
                  onChange={e => setPromoModal(prev => ({ ...prev, form: { ...prev.form, title: e.target.value } }))}
                />
              </div>
              <div className="admin-field">
                <label className="admin-field-label" htmlFor="promo-message">Message *</label>
                <textarea
                  id="promo-message"
                  rows="3"
                  className="reschedule-textarea reschedule-textarea--short"
                  placeholder="e.g. 20% off all facials this month — book now!"
                  value={promoModal.form.message}
                  onChange={e => setPromoModal(prev => ({ ...prev, form: { ...prev.form, message: e.target.value } }))}
                />
              </div>
              {promoModal.form.type === 'section' && (
                <div className="admin-field">
                  <span className="admin-field-label">Photo (optional)</span>

                  {/* Preview of currently selected image */}
                  {promoModal.form.image_url && (
                    <div className="banner-preview">
                      <img
                        src={promoModal.form.image_url}
                        alt="Banner preview"
                      />
                      <button
                        type="button"
                        className="thumb-remove thumb-remove--onimage"
                        aria-label="Remove photo"
                        onClick={() => setPromoModal(prev => ({ ...prev, form: { ...prev.form, image_url: '' } }))}
                      >×</button>
                    </div>
                  )}

                  {/* Upload + browse row */}
                  <div className="upload-row">
                    <label className={`upload-drop ${bannerUploading ? 'busy' : ''}`}>
                      {bannerUploading ? 'Uploading…' : '↑ Upload from computer'}
                      <input
                        type="file"
                        accept="image/*"
                        className="visually-hidden"
                        disabled={bannerUploading}
                        onChange={e => { if (e.target.files[0]) uploadBannerImage(e.target.files[0]); e.target.value = ''; }}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-plain"
                      onClick={() => { const next = !showBannerGallery; setShowBannerGallery(next); if (next) loadBannerImages(); }}
                    >
                      {showBannerGallery ? 'Hide gallery' : 'Browse uploads'}
                    </button>
                  </div>

                  {/* Previously uploaded images gallery */}
                  {showBannerGallery && (
                    <div className="gallery-grid">
                      {bannerImages.length === 0 ? (
                        <div className="gallery-empty">No images uploaded yet</div>
                      ) : bannerImages.map(img => (
                        <button
                          key={img.name}
                          type="button"
                          className={`gallery-tile ${promoModal.form.image_url === img.url ? 'selected' : ''}`}
                          onClick={() => { setPromoModal(prev => ({ ...prev, form: { ...prev.form, image_url: img.url } })); setShowBannerGallery(false); }}
                          title={img.name}
                          aria-label={`Use image ${img.name}`}
                        >
                          <img src={img.url} alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="admin-field">
                <label className="admin-field-label" htmlFor="promo-colour">Background colour</label>
                <div className="colour-row">
                  <input
                    id="promo-colour"
                    type="color"
                    value={promoModal.form.bg_color}
                    onChange={e => setPromoModal(prev => ({ ...prev, form: { ...prev.form, bg_color: e.target.value } }))}
                    className="colour-input"
                  />
                  <span className="admin-field-note">{promoModal.form.bg_color}</span>
                </div>
              </div>
              <label className="admin-check">
                <input
                  type="checkbox"
                  checked={promoModal.form.is_active}
                  onChange={e => setPromoModal(prev => ({ ...prev, form: { ...prev.form, is_active: e.target.checked } }))}
                />
                Make live immediately
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-modal-approve" onClick={handleSavePromo}>
                {promoModal.editing ? '✓ Save Changes' : '+ Add Promotion'}
              </button>
              <button className="btn-modal-cancel" onClick={closePromoModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DECLINE MODAL ==================== */}
      {declineModal.isOpen && (
        <div className="modal-overlay" onClick={isBusy ? undefined : closeDeclineModal}>
          <div className="modal-content reschedule-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Decline Booking Request</h2>
              <button className="modal-close" onClick={closeDeclineModal} disabled={isBusy}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-client-name">
                <strong>{declineModal.booking?.name}</strong>
              </p>
              <p className="modal-info">
                Requested: {formatDate(declineModal.booking?.date)}, {formatTimeRange(
                  declineModal.booking?.time_range_start,
                  declineModal.booking?.time_range_end
                )}
              </p>

              <div className="reschedule-form">
                <label htmlFor="decline-message">
                  Optional message to client:
                </label>
                <textarea
                  id="decline-message"
                  value={declineModal.message}
                  onChange={(e) => setDeclineModal(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Add a personal message (optional)..."
                  rows="4"
                  className="reschedule-textarea"
                />
                <p className="reschedule-hint">
                  If you send an email, {declineModal.booking?.name} will receive a notification with your message and their appointment details.
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-modal-decline" onClick={handleDeclineWithMessage} disabled={isBusy}>
                {actionBusy === 'decline-email' ? 'Declining…' : '✕ Decline & Send Email'}
              </button>
              <button
                className="btn-modal-decline"
                style={{ background: '#6b7280' }}
                onClick={handleDeclineWithoutEmail}
                disabled={isBusy}
              >
                {actionBusy === 'decline-silent' ? 'Declining…' : '✕ Decline Without Email'}
              </button>
              <button className="btn-modal-cancel" onClick={closeDeclineModal} disabled={isBusy}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;